import { entrarConSesionCompartida, borrarSesion } from '../ui/sesion.js';
import Entrando from '../ui/Entrando.jsx';
import React, { useState, useMemo, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { C, GRAD } from '../ui/theme.js';
import { EmptyState, SectionTitle } from '../ui/ui.jsx';
import { StatCard } from '../ui/premium.jsx';

// ==================== SUPABASE CONFIG ====================
const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

const supabase = {
  from: (table) => ({
    select: async (columns = '*') => {
      try {
        const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=${columns}`, {
          headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        return { data: await response.json(), error: null };
      } catch (error) { return { data: null, error }; }
    },
    insert: async (records) => {
      try {
        const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
          method: 'POST',
          headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', 'Prefer': 'return=representation' },
          body: JSON.stringify(Array.isArray(records) ? records : [records])
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        return { data: await response.json(), error: null };
      } catch (error) { return { data: null, error }; }
    },
    update: async (record) => ({
      eq: async (column, value) => {
        try {
          const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${column}=eq.${value}`, {
            method: 'PATCH',
            headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', 'Prefer': 'return=representation' },
            body: JSON.stringify(record)
          });
          if (!response.ok) throw new Error(`Error ${response.status}`);
          return { data: await response.json(), error: null };
        } catch (error) { return { data: null, error }; }
      }
    }),
    delete: async () => ({
      eq: async (column, value) => {
        try {
          const response = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${column}=eq.${value}`, {
            method: 'DELETE',
            headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
          });
          if (!response.ok) throw new Error(`Error ${response.status}`);
          return { error: null };
        } catch (error) { return { error }; }
      }
    })
  })
};

// ==================== SUPABASE AUTH ====================
const supabaseAuth = {
  signIn: async (email, password) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error_description || 'Credenciales incorrectas' };
      }
      const data = await response.json();
      localStorage.setItem('sb-access-token', data.access_token);
      localStorage.setItem('sb-refresh-token', data.refresh_token);
      return { data, error: null };
    } catch (error) { return { data: null, error: error.message }; }
  },
  signOut: async () => {
    const token = localStorage.getItem('sb-access-token');
    if (token) { try { await fetch(`${SUPABASE_URL}/auth/v1/logout`, { method: 'POST', headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}` } }); } catch {} }
    localStorage.removeItem('sb-access-token');
    localStorage.removeItem('sb-refresh-token');
  },
  getCurrentUser: async () => {
    const token = localStorage.getItem('sb-access-token');
    if (!token) return null;
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}` } });
      if (!response.ok) return null;
      return await response.json();
    } catch { return null; }
  }
};

const findAdminByAuthId = async (authUserId) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/administradores?auth_user_id=eq.${authUserId}&activo=eq.true&select=*`;
    const response = await fetch(url, { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` } });
    const data = await response.json();
    return data && data.length > 0 ? data[0] : null;
  } catch { return null; }
};

// ==================== DATA ====================
const ORIGENES = [
  { nombre: 'Carnovo', color: '#1E3A5F', tipo: 'portal' },
  { nombre: 'Coches.net', color: '#FF6B35', tipo: 'portal' },
  { nombre: 'Autocasión', color: '#808080', tipo: 'portal' },
  { nombre: 'Wallapop', color: '#13C1AC', tipo: 'portal' },
  { nombre: 'CUSTOMER PEUGEOT', color: '#0066B3', tipo: 'customer' },
  { nombre: 'CUSTOMER CITROEN', color: '#AC0F25', tipo: 'customer' },
  { nombre: 'CUSTOMER OPEL', color: '#9aa4af', tipo: 'customer' },
  { nombre: 'CUSTOMER JEEP', color: '#2F4F4F', tipo: 'customer' },
  { nombre: 'CUSTOMER FIAT', color: '#005187', tipo: 'customer' },
  { nombre: 'Facebook', color: '#1877F2', tipo: 'social' },
  { nombre: 'Instagram', color: '#E4405F', tipo: 'social' },
  { nombre: 'Web Leapmotor', color: '#00A0E3', tipo: 'web' },
  { nombre: 'Web Dimoauto', color: '#6B0000', tipo: 'web' },
  { nombre: 'StockSpark', color: '#FF9500', tipo: 'portal' }
];

const ESTADOS = {
  'Pendiente': { color: '#8a929c', textColor: '#fff' },
  'Atendida': { color: '#e08c1e', textColor: '#000' },
  'Citado': { color: '#7a5bd0', textColor: '#fff' },
  'Ofertado': { color: '#1a9cd4', textColor: '#fff' },
  'Cancelada Comercial': { color: '#d23b34', textColor: '#fff' },
  'Nurturing': { color: '#d96ba8', textColor: '#fff' },
  'Vendido': { color: '#2e9e5b', textColor: '#fff' },
  'En curso': { color: '#1a9cd4', textColor: '#fff' },
  'Cancelada': { color: '#8a929c', textColor: '#fff' }
};

const MARCAS = ['Peugeot', 'Citroën', 'Opel', 'Fiat', 'Jeep', 'Abarth', 'Leapmotor'];
const TIPOS_VENTA = ['VN', 'VO', 'B2B'];
const CENTROS = ['DIMOAUTO Hospitalet', 'DIMOAUTO Molins de rei', 'JP CARRION Hospitalet', 'JP CARRION Molins de rei', 'MAAM Cornellà de Llobregat', 'MAAM L\'Hospitalet de Llobregat', 'Dimoauto Flotas'];

// ==================== EMAILJS CONFIG ====================
// Configurado para Grupo Dimoauto
const EMAILJS_CONFIG = {
  enabled: true,
  serviceId: 'service_xxn17h1',
  templateId: 'template_rpzw4fr',
  publicKey: 'sWKqE0R5hzfSrgnme'
};

// Direcciones de centros para el email
const CENTROS_INFO = {
  'DIMOAUTO Hospitalet': { direccion: 'Rambla Marina 442, 08907 Hospitalet de Llobregat', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'DIMOAUTO Molins de rei': { direccion: 'C/ Major 50, 08750 Molins de Rei', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'JP CARRION Hospitalet': { direccion: 'Rambla Marina 442, 08907 Hospitalet de Llobregat', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'JP CARRION Molins de rei': { direccion: 'C/ Major 50, 08750 Molins de Rei', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'MAAM Cornellà de Llobregat': { direccion: 'Av. Baix Llobregat 78, 08940 Cornellà', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  "MAAM L'Hospitalet de Llobregat": { direccion: "Rambla Just Oliveras 45, 08901 L'Hospitalet", horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'Dimoauto Flotas': { direccion: 'Rambla Marina 442, 08907 Hospitalet de Llobregat', horarios: 'L-V 9:00-18:00' }
};

const CRITERIOS_SELECCION = [
  { value: 'menor_carga', label: 'Menor carga', icon: '📊', description: 'Al comercial con menos leads activos' },
  { value: 'mas_rapido', label: 'Más rápido', icon: '⚡', description: 'Al que responde más rápido' },
  { value: 'mejor_conversion', label: 'Mejor conversión', icon: '🎯', description: 'Al que más vende' },
  { value: 'round_robin', label: 'Rotación', icon: '🔄', description: 'Reparto alternado' },
  { value: 'score_combinado', label: 'Score IA', icon: '🧠', description: 'Carga 40% + Velocidad 30% + Conversión 30%' },
  { value: 'preferente_pool', label: 'Preferente con cupo', icon: '⭐', description: 'Al primero del pool mientras tenga < max_leads_activos. Si saturado, al siguiente.' }
];

// ==================== VISTA POR MARCA (constantes) ====================
const MARCAS_ORDEN = ['Peugeot', 'Citroën', 'Opel', 'Fiat', 'Jeep', 'Fiat Pro', 'Abarth', 'Leapmotor'];
const MARCA_COLORS = { 'Peugeot': '#0a4595', 'Citroën': '#c8102e', 'Opel': '#f7ff14', 'Fiat': '#9b2743', 'Jeep': '#1f4d2e', 'Fiat Pro': '#41607a', 'Abarth': '#b51b1b', 'Leapmotor': '#00b2a9' };
const MARCA_TEXT_DARK = { 'Opel': true };
const CREDENCIALES_C360 = [
  { titulo: 'Peugeot', usuario: 'ES-000018P.d046', marcas: ['Peugeot'], tipo: 'Credencial propia' },
  { titulo: 'Citroën', usuario: 'OBSFARM\\ES-000402C.d006', marcas: ['Citroën'], tipo: 'Credencial propia' },
  { titulo: 'Cuenta compartida', usuario: 'ES-0705881.d036', marcas: ['Opel', 'Fiat', 'Jeep', 'Fiat Pro', 'Abarth'], tipo: '5 marcas · compartida' },
];
const CARGA_ALTA = 120;
const CARGA_CRITICA = 200;

// ==================== HELPERS ====================
const formatDateTime = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return isNaN(d.getTime()) ? '-' : `${d.toLocaleDateString('es-ES')} ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatRelativeTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const d = new Date(date);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const getOrigenColor = (origen) => ORIGENES.find(o => o.nombre === origen)?.color || '#808080';

// ── Formas del sistema de diseño de la suite ───────────────────────────────
// Solo presentación: mismos datos, misma disposición, la forma de siempre del
// resto de apps del grupo (radios 10 en botones y campos, 12 en tarjetas, 999
// en píldoras).
const pildora = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
  padding: '3px 10px', borderRadius: 999, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px',
  color, background: `${color}18`, border: `1px solid ${color}44`,
});
const botonPrimario = {
  background: C.brand, color: '#fff', border: 'none', borderRadius: 10,
  padding: '9px 16px', fontSize: '13px', fontWeight: 800, cursor: 'pointer',
  boxShadow: '0 3px 10px rgba(0,81,135,.28)',
};
const botonSecundario = {
  background: '#fff', color: C.page, border: `1px solid ${C.line}`, borderRadius: 10,
  padding: '9px 16px', fontSize: '13px', fontWeight: 800, cursor: 'pointer',
};
const tarjeta = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12 };
const campo = {
  border: `1px solid ${C.line}`, borderRadius: 10, background: '#fff',
  fontSize: '13px', padding: '8px 12px', color: C.ink, boxSizing: 'border-box',
};
// Tabla: cabecera gris clara en versalitas y filas separadas por una línea fina.
const celdaTitulo = {
  padding: '9px 12px', textAlign: 'left', background: '#f7f8fa', color: C.muted,
  fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase',
  borderBottom: `1px solid ${C.line}`,
};
const SEPARADOR_FILA = '1px solid #eaecef';
// Etiqueta pequeña de las tarjetas de cifra (misma forma que StatCard).
const etiquetaCifra = { fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px', color: C.muted, textTransform: 'uppercase' };

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

// Helper unificado: ¿este lead está pendiente de asignar en el inbox?
// Importante: el campo `estado` puede venir con casing distinto desde n8n / backend
// ('Asignado','Rechazado','Pendiente') o desde la propia app ('asignado','rechazado').
// Por eso se normaliza a lowercase antes de comparar. Y si ya tiene comercial,
// se considera no-pendiente aunque el `estado` no se haya actualizado todavía.
const isPendingInboxLead = (l) => {
  if (!l) return false;
  if (l.comercial_asignado) return false;
  const e = (l.estado || '').toLowerCase();
  return e !== 'asignado' && e !== 'rechazado';
};

// ==================== FETCH FUNCTIONS ====================
const fetchLeads = async () => {
  try {
    // PostgREST limita a 1000 filas/request por defecto. El shim 'supabase'
    // no soporta .order/.range/.limit; paginamos con fetch directo.
    const PAGE_SIZE = 500;
    const MAX_ROWS = 100000;
    const rows = [];
    let offset = 0;
    while (offset < MAX_ROWS) {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/leads?select=*&offset=${offset}&limit=${PAGE_SIZE}`, {
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) break;
      rows.push(...data);
      offset += data.length;
    }
    return rows;
  } catch (e) { console.error(e); return []; }
};
const fetchComerciales = async () => { try { const { data, error } = await supabase.from('comerciales').select('*'); if (error) throw error; return (data || []).filter(c => c.activo); } catch (e) { console.error(e); return []; } };
const fetchAdministradores = async () => { try { const { data, error } = await supabase.from('administradores').select('*'); if (error) throw error; return (data || []).filter(a => a.activo); } catch (e) { console.error(e); return null; } };
// PostgREST corta a 1000 filas/request y el shim 'supabase' no soporta
// .order/.range/.limit. Sin paginar solo cargábamos las primeras 1000 filas
// del inbox (la tabla ya supera ese tamaño), así que los leads recién
// asignados (id más alto) NO aparecían en "Asignados". Paginamos con fetch
// directo, igual que fetchLeads, con order=id.asc para paginación estable.
const fetchInboxLeads = async () => {
  try {
    const PAGE_SIZE = 500;
    const MAX_ROWS = 100000;
    const rows = [];
    let offset = 0;
    while (offset < MAX_ROWS) {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/AutoLead_inbox?select=*&order=id.asc&offset=${offset}&limit=${PAGE_SIZE}`, {
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) break;
      rows.push(...data);
      offset += data.length;
    }
    return rows;
  } catch (e) { console.error(e); return []; }
};
const fetchReglas = async () => { try { const { data, error } = await supabase.from('AutoLead_reglas').select('*'); if (error) throw error; return data || []; } catch (e) { console.error(e); return []; } };
const fetchComercialesStats = async () => { try { const { data, error } = await supabase.from('AutoLead_comerciales_stats').select('*'); if (error) throw error; return data || []; } catch (e) { console.error(e); return []; } };
const fetchProximasAsignaciones = async () => { try { const { data, error } = await supabase.from('AutoLead_proxima_asignacion').select('*'); if (error) throw error; return data || []; } catch (e) { console.error(e); return []; } };
// PATCH 2026-06-22: lee UN lead del inbox por id (sin el tope de 1000 filas de
// PostgREST). Las verificaciones de asignación hacían select('*') del inbox
// completo y find(id) -> los leads más nuevos (id alto) caían fuera de las
// primeras 1000 y daban falso "El lead ya no existe en el sistema".
const fetchInboxLeadById = async (id) => {
  try {
    const resp = await fetch(`${SUPABASE_URL}/rest/v1/AutoLead_inbox?select=*&id=eq.${id}`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
    });
    if (!resp.ok) return null;
    const arr = await resp.json();
    return (Array.isArray(arr) && arr.length) ? arr[0] : null;
  } catch (e) { return null; }
};
const insertLead = async (lead) => { try { const { data, error } = await supabase.from('leads').insert(lead); if (error) throw error; return { data, error: null }; } catch (e) { return { data: null, error: e }; } };
// Devuelve el siguiente lead_id secuencial: max numérico existente + 1.
//
// PATCH 2026-05-16: el método anterior (ORDER BY lead_id.desc + parseInt)
// rompió producción durante 5 días porque `lead_id` es TEXT y un solo lead
// con `lead_id = "lead-1778494740355"` (fallback antiguo Date.now() de esta
// misma función — incidente Jaime Felip / MARTIA 2026-05-11) hace que el
// ORDER text-DESC devuelva ese string como top, `parseInt(...)` da NaN,
// fallback a 0, y la función retorna "1" eternamente → 409 leads_lead_id_key
// cada 5 s en el polling. Saturó Supabase y rompió cambios de estado /
// bloqueo de agenda en CRM Comerciales hasta el 16-may.
//
// Solución: llamar a la RPC `get_next_lead_id_numeric` (la misma que usa el
// Edge Function `auto-assign-lead` v8+); hace MAX(lead_id::int) ignorando
// textos no numéricos y devuelve el siguiente. Fallback HTTP solo si la RPC
// no está disponible — y aun así filtramos los lead_id no numéricos antes
// de parsear.
const getNextLeadId = async () => {
  try {
    const rpcResp = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_next_lead_id_numeric`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: '{}'
    });
    if (rpcResp.ok) {
      const next = await rpcResp.json();
      // La RPC devuelve text. Aceptamos string o number.
      const s = typeof next === 'string' ? next : (next != null ? String(next) : '');
      if (s && /^[0-9]+$/.test(s)) return s;
    }
  } catch (_) { /* cae al fallback de abajo */ }

  // Fallback defensivo: traemos hasta 50 lead_ids y nos quedamos con el
  // MAX numérico filtrando textos no parseables. No usamos `order=lead_id.desc`
  // (text-DESC) porque ese fue exactamente el bug original.
  try {
    const response = await fetch(`${SUPABASE_URL}/rest/v1/leads?select=lead_id&limit=50&order=id.desc`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    const data = await response.json();
    let max = 0;
    for (const row of (Array.isArray(data) ? data : [])) {
      const v = row && row.lead_id;
      if (typeof v === 'string' && /^[0-9]+$/.test(v)) {
        const n = parseInt(v, 10);
        if (Number.isFinite(n) && n > max) max = n;
      }
    }
    if (max > 0) return String(max + 1);
  } catch (e) {
    console.error('Error getNextLeadId (fallback HTTP):', e);
  }

  // Último recurso: prefijo `lead-` con timestamp para que sea único y
  // queda fácilmente identificable como anomalía. NO devolvemos un id
  // puramente numérico bajo (p.ej. "1") porque colisiona contra los lead_id
  // existentes y reactiva el loop 409.
  return `lead-${Date.now()}`;
};
const updateInboxLead = async (id, updates) => { 
  try { 
    const fn = await supabase.from('AutoLead_inbox').update(updates); 
    const { data, error } = await fn.eq('id', id); 
    if (error) throw error;
    return { data, error: null }; 
  } catch (e) { 
    console.error('Error actualizando AutoLead_inbox:', e);
    return { data: null, error: e }; 
  } 
};
const deleteInboxLead = async (id) => {
  try {
    const fn = await supabase.from('AutoLead_inbox').delete();
    const { error } = await fn.eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (e) {
    console.error('Error eliminando AutoLead_inbox:', e);
    return { error: e };
  }
};
const insertRegla = async (regla) => { try { const { data, error } = await supabase.from('AutoLead_reglas').insert(regla); if (error) throw error; return { data, error: null }; } catch (e) { return { data: null, error: e }; } };
const updateRegla = async (id, updates) => { try { const fn = await supabase.from('AutoLead_reglas').update(updates); const { data, error } = await fn.eq('id', id); if (error) throw error; return { data, error: null }; } catch (e) { return { data: null, error: e }; } };
const deleteRegla = async (id) => { try { const fn = await supabase.from('AutoLead_reglas').delete(); const { error } = await fn.eq('id', id); if (error) throw error; return { error: null }; } catch (e) { return { error: e }; } };

// ==================== EMAIL SERVICE ====================
// Función para registrar email enviado en la base de datos de costes
const registrarEmailEnviado = async (lead, comercial) => {
  const SUPABASE_COSTES_URL = 'https://vgraqyqwrdgsqdkpltou.supabase.co';
  const SUPABASE_COSTES_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZncmFxeXF3cmRnc3Fka3BsdG91Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjcwMjgyMjIsImV4cCI6MjA4MjYwNDIyMn0.PQ9BV4f3s_wOoR4YKkeP0C5yW1pLXF-6CD7174Dvebc';
  
  try {
    // Registrar en costes_resumen_diario
    const registro = {
      fecha: new Date().toISOString().split('T')[0], // Solo fecha YYYY-MM-DD
      aplicacion_id: 3, // ID de la aplicación AutoLeads
      componente_id: 19, // ID del componente EmailJS AutoLeads
      ejecuciones: 1,
      coste_eur: 0.001 // Coste aproximado por email con EmailJS
    };

    const response = await fetch(`${SUPABASE_COSTES_URL}/rest/v1/costes_resumen_diario`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_COSTES_KEY,
        'Authorization': `Bearer ${SUPABASE_COSTES_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify(registro)
    });

    if (!response.ok) {
      console.warn('No se pudo registrar el email en costes:', response.status);
    } else {
      console.log('✅ Email registrado en sistema de costes');
    }
  } catch (error) {
    console.warn('Error registrando email en costes:', error);
    // No bloqueamos el flujo si falla el registro
  }
};

const sendLeadEmail = async (lead, comercial) => {
  if (!EMAILJS_CONFIG.enabled) {
    console.log('📧 EmailJS desactivado - configurar en EMAILJS_CONFIG');
    return { success: false, reason: 'disabled' };
  }
  
  if (!comercial.email) {
    console.log('📧 Comercial sin email, no se envía notificación');
    return { success: false, reason: 'no_email' };
  }
  
  // Cargar EmailJS si no está
  if (!window.emailjs) {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    document.head.appendChild(script);
    await new Promise(resolve => script.onload = resolve);
    window.emailjs.init(EMAILJS_CONFIG.publicKey);
  }

  const centroInfo = CENTROS_INFO[comercial.centro] || { direccion: comercial.centro || '', horarios: '' };
  const now = new Date();
  
  // Preparar parámetros del template
  // LEAD viene de AutoLead_inbox, COMERCIAL viene de tabla comerciales
  const templateParams = {
    // Destinatario
    to_email: comercial.email,
    to_name: comercial.nombre,
    
    // Subject
    subject: `Nuevo Lead ${lead.origen || ''}: ${lead.nombre || 'Cliente'} - ${lead.marca || ''} ${lead.modelo || ''}`,
    
    // CITA (de AutoLead_inbox: fecha_cita, hora_cita, tipo_cita)
    fecha_cita: lead.fecha_cita ? new Date(lead.fecha_cita).toLocaleDateString('es-ES') : now.toLocaleDateString('es-ES'),
    hora_cita: lead.hora_cita || now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
    tipo_cita: lead.tipo_cita || 'Lead Válido',
    
    // COMERCIAL ASIGNADO (de tabla comerciales: nombre, apellidos, telefono, email, cargo)
    comercial_nombre: comercial.nombre || '',
    comercial_apellidos: comercial.apellidos || comercial.cargo || '', // Usa cargo si no hay apellidos
    comercial_telefono: comercial.telefono || '',
    comercial_email: comercial.email || '',
    
    // OBSERVACIONES (de AutoLead_inbox: origen, observaciones)
    origen: lead.origen || 'Portal',
    
    // CLIENTE (de AutoLead_inbox: razon_social, nombre, apellidos)
    razon_social: lead.razon_social || '',
    cliente_nombre: lead.nombre || '',
    cliente_apellidos: lead.apellidos || '',
    
    // CONTACTO (de AutoLead_inbox: telefono, movil, email)
    telefono: lead.telefono || '',
    movil: lead.movil || lead.telefono || '',
    cliente_email: lead.email || '',
    
    // VEHÍCULO (de AutoLead_inbox: marca, modelo, motor, matricula, kilometros)
    marca: lead.marca || '',
    modelo: lead.modelo || '',
    motor: lead.motor || '',
    matricula: lead.matricula || '',
    kilometros: lead.kilometros ? String(lead.kilometros) : '0',
    
    // UBICACIÓN (de tabla comerciales: centro + CENTROS_INFO)
    centro_nombre: comercial.centro || '',
    centro_direccion: centroInfo.direccion,
    centro_horarios: centroInfo.horarios
  };

  try {
    const response = await window.emailjs.send(EMAILJS_CONFIG.serviceId, EMAILJS_CONFIG.templateId, templateParams);
    console.log('✅ Email enviado a', comercial.email);
    
    // Registrar el envío en el sistema de costes
    await registrarEmailEnviado(lead, comercial);
    
    return { success: true, response };
  } catch (error) {
    console.error('❌ Error enviando email:', error);
    return { success: false, error };
  }
};

// ==================== CABECERA ====================
// La marca la pone el menú lateral de la suite: aquí solo el nombre de la app
// y una línea que explica para qué sirve.
const Header = ({ onRefresh, isLoading, lastUpdate, admin, onLogout, onOpenChangePassword, autoAssignEnabled, onToggleAutoAssign, lastAutoAssign, realtimeConnected }) => {
  return (
    <header style={{background: C.card, padding: '14px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', borderBottom: `1px solid ${C.line}`}}>
      <div>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
          <h1 style={{fontSize: '19px', fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-0.2px'}}>Auto Leads</h1>
          <span style={pildora(realtimeConnected ? C.verde : C.naranja)}>
            <span className={realtimeConnected ? 'fg-breath' : ''} style={{width: 6, height: 6, borderRadius: '50%', background: realtimeConnected ? C.verde : C.naranja}}></span>
            {realtimeConnected ? 'Realtime' : 'Conectando...'}
          </span>
        </div>
        <div style={{fontSize: '12.5px', color: C.muted, marginTop: '3px'}}>Reparto automático de los leads entrantes entre los comerciales del grupo</div>
      </div>
      <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
        <button
          onClick={onToggleAutoAssign}
          className="fg-nav"
          style={{
            background: autoAssignEnabled ? C.verde : '#fff',
            border: autoAssignEnabled ? 'none' : `1px solid ${C.line}`,
            borderRadius: 10,
            padding: '7px 14px',
            color: autoAssignEnabled ? '#fff' : C.muted,
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: autoAssignEnabled ? '0 3px 10px rgba(46,158,91,.28)' : 'none'
          }}
        >
          <span style={{fontSize: '13px'}}>{autoAssignEnabled ? '🤖' : '⏸️'}</span>
          Auto-asignar {autoAssignEnabled ? 'ON' : 'OFF'}
        </button>
        {lastAutoAssign && (
          <span style={pildora(C.verde)}>
            ✓ {lastAutoAssign.count} asignados {lastAutoAssign.time}
          </span>
        )}
        {lastUpdate && <span style={{fontSize: '11px', color: C.faint}}>{lastUpdate}</span>}
        {admin && (
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div onClick={onOpenChangePassword} className="fg-nav" style={{display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#fff', borderRadius: 999, fontSize: '12px', fontWeight: 700, color: C.page, border: `1px solid ${C.line}`, cursor: 'pointer'}} title="Clic para cambiar contraseña"><span>👤</span><span>{admin.nombre}</span><span style={{fontSize: '10px', opacity: 0.6}}>🔐</span></div>
            <button onClick={onLogout} style={{...botonSecundario, padding: '7px 12px', color: C.rojo}}>🚪 Salir</button>
          </div>
        )}
      </div>
    </header>
  );
};

// ==================== PESTAÑAS ====================
// Navegación interna de la app en píldoras: la activa lleva el gradiente de
// marca, las demás se quedan transparentes.
const Tabs = ({ activeTab, setActiveTab, inboxCount, assignedCount }) => {
  const items = [
    { id: 'inbox', label: 'Pendientes', icon: '📥', badge: inboxCount },
    { id: 'asignados', label: 'Asignados', icon: '✅', badge: assignedCount },
    { id: 'comerciales', label: 'Comerciales', icon: '👥' },
    { id: 'reglas', label: 'Reglas', icon: '⚙️' },
    { id: 'vista-marca', label: 'Por marca', icon: '🏷️' },
    { id: 'origenes', label: 'Orígenes', icon: '📊' },
  ];
  return (
    <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', padding: '10px 20px', background: C.card, borderBottom: `1px solid ${C.line}`}}>
      {items.map(item => {
        const activa = activeTab === item.id;
        return (
          <button key={item.id} onClick={() => setActiveTab(item.id)} className="fg-nav" style={{
            display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 14px',
            border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: '13px', fontWeight: 700,
            background: activa ? GRAD : 'transparent', color: activa ? '#fff' : C.page,
            boxShadow: activa ? '0 6px 16px rgba(0,81,135,.28)' : 'none',
          }}>
            <span style={{fontSize: '14px'}}>{item.icon}</span>
            <span>{item.label}</span>
            {item.badge > 0 && (
              <span style={activa
                ? {borderRadius: 999, padding: '1px 7px', fontSize: '10.5px', fontWeight: 800, background: 'rgba(255,255,255,0.22)', color: '#fff'}
                : pildora(item.id === 'inbox' ? C.naranja : C.verde)}>{item.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ==================== MULTI SELECT ====================
const MultiSelectComerciales = ({ comerciales, selected, onChange, comercialesStats }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleComercial = (id) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };
  const getStats = (id) => comercialesStats.find(s => s.comercial_id === id) || { leads_activos: 0, tasa_conversion_30d: 0 };

  return (
    <div style={{position: 'relative'}}>
      <div onClick={() => setIsOpen(!isOpen)} style={{...campo, width: '100%', minHeight: '42px', cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center'}}>
        {selected.length === 0 ? (
          <span style={{color: C.faint, fontSize: '13px'}}>Seleccionar comerciales...</span>
        ) : (
          selected.map(id => {
            const c = comerciales.find(x => x.id === id);
            return c ? (
              <span key={id} style={pildora(C.brand)}>
                {c.nombre}
                <span onClick={(e) => { e.stopPropagation(); toggleComercial(id); }} style={{cursor: 'pointer', fontWeight: 800}}>×</span>
              </span>
            ) : null;
          })
        )}
        <span style={{marginLeft: 'auto', color: C.muted}}>▼</span>
      </div>
      {isOpen && (
        <div style={{position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, marginTop: '4px', maxHeight: '250px', overflowY: 'auto', zIndex: 100, boxShadow: '0 14px 30px rgba(15,42,70,.12)'}}>
          {comerciales.map(c => {
            const stats = getStats(c.id);
            const isSelected = selected.includes(c.id);
            return (
              <div key={c.id} onClick={() => toggleComercial(c.id)} className="fg-nav" style={{padding: '10px 12px', cursor: 'pointer', background: isSelected ? '#f0f6fa' : '#fff', borderBottom: SEPARADOR_FILA, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <span style={{width: '20px', height: '20px', borderRadius: 8, border: isSelected ? 'none' : `2px solid ${C.line}`, background: isSelected ? C.brand : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px'}}>{isSelected && '✓'}</span>
                  <div>
                    <div style={{fontWeight: 700, fontSize: '12.5px', color: C.ink}}>{c.nombre}</div>
                    <div style={{fontSize: '11px', color: C.muted}}>{c.centro}</div>
                  </div>
                </div>
                <div style={{textAlign: 'right', fontSize: '11px'}}>
                  <div style={{color: stats.leads_activos > 15 ? C.rojo : C.verde, fontWeight: 800}}>{stats.leads_activos} activos</div>
                  <div style={{color: C.muted}}>{((stats.tasa_conversion_30d || 0) * 100).toFixed(0)}% conv.</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
// ==================== LEAD DETAIL ====================
const LeadDetail = ({ lead, comerciales, onAssign, onReject, suggestedComercial, matchInfo }) => {
  const [selectedComercial, setSelectedComercial] = useState(suggestedComercial?.id || '');
  const [isAssigning, setIsAssigning] = useState(false);
  useEffect(() => { setSelectedComercial(suggestedComercial?.id || ''); }, [suggestedComercial, lead]);

  const handleAssign = async () => {
    if (!selectedComercial) return;
    setIsAssigning(true);
    const comercial = comerciales.find(c => c.id === parseInt(selectedComercial));
    if (comercial) await onAssign(lead, comercial);
    setIsAssigning(false);
  };

  return (
    <div style={{padding: '24px'}}>
      <div className="fg-card" style={{...tarjeta, padding: '20px', marginBottom: '16px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '16px'}}>
          <div>
            <div style={{display: 'flex', gap: '8px', marginBottom: '8px'}}>
              <span style={pildora(getOrigenColor(lead.origen))}>{lead.origen}</span>
              {lead.tipo_venta && <span style={pildora(lead.tipo_venta === 'VN' ? C.brand : lead.tipo_venta === 'B2B' ? C.verde : C.naranja)}>{lead.tipo_venta}</span>}
            </div>
            <h2 style={{fontSize: '19px', fontWeight: 800, color: C.ink, margin: '0 0 4px'}}>{lead.nombre} {lead.apellidos}</h2>
            <p style={{fontSize: '12.5px', color: C.muted, margin: 0}}>{lead.marca} {lead.modelo}</p>
          </div>
          <div style={{textAlign: 'right', fontSize: '12px', color: C.muted}}>{formatDateTime(lead.fecha_recepcion)}</div>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px'}}>
          {lead.email && <div style={{padding: '12px', background: C.bg, borderRadius: 10}}><div style={etiquetaCifra}>Email</div><div style={{fontSize: '12.5px', fontWeight: 700, color: C.ink, marginTop: '4px'}}>{lead.email}</div></div>}
          {(lead.telefono || lead.movil) && <div style={{padding: '12px', background: C.bg, borderRadius: 10}}><div style={etiquetaCifra}>Teléfono</div><div style={{fontSize: '12.5px', fontWeight: 700, color: C.ink, marginTop: '4px'}}>{lead.telefono || lead.movil}</div></div>}
        </div>
        {matchInfo && (
          <div style={{background: `${C.verde}14`, border: `1px solid ${C.verde}44`, borderRadius: 12, padding: '12px', marginBottom: '16px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}><span style={{fontSize: '16px'}}>🧠</span><span style={{fontWeight: 800, color: C.verde, fontSize: '12.5px'}}>Asignación inteligente</span></div>
            <div style={{fontSize: '12.5px', color: C.page, lineHeight: 1.5}}>
              <div><strong>Regla:</strong> {matchInfo.regla?.nombre || 'Match'}</div>
              <div><strong>Criterio:</strong> {CRITERIOS_SELECCION.find(c => c.value === matchInfo.regla?.criterio_seleccion)?.label || 'Menor carga'}</div>
              <div><strong>Seleccionado:</strong> {matchInfo.comercial?.nombre} ({matchInfo.razon})</div>
            </div>
          </div>
        )}
        <div style={{marginBottom: '16px'}}>
          <label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '6px'}}>Asignar a comercial</label>
          <select value={selectedComercial} onChange={(e) => setSelectedComercial(e.target.value)} style={{...campo, width: '100%', padding: '11px 12px'}}>
            <option value="">Seleccionar...</option>
            {comerciales.map(c => <option key={c.id} value={c.id}>{c.nombre} - {c.centro}</option>)}
          </select>
        </div>
        <div style={{display: 'flex', gap: '12px'}}>
          <button onClick={handleAssign} disabled={!selectedComercial || isAssigning} style={{...botonPrimario, flex: 1, padding: '11px 16px', background: selectedComercial ? C.brand : C.line, color: selectedComercial ? '#fff' : C.faint, boxShadow: selectedComercial ? botonPrimario.boxShadow : 'none', cursor: selectedComercial ? 'pointer' : 'not-allowed'}}>{isAssigning ? '⏳ Asignando...' : '✓ Asignar'}</button>
          <button onClick={() => onReject(lead)} style={{...botonSecundario, padding: '11px 20px', color: C.rojo}}>✕ Rechazar</button>
        </div>
      </div>
    </div>
  );
};

// ── Helper: matcher de reglas vía SQL (fn_match_reglas) ────────────────
// Llama a la RPC PostgREST (rol anon, EXECUTE concedido). Devuelve el array
// ORDENADO de regla_id candidatos, o null si la llamada falla (para que el
// llamador pueda caer a un matcher local de respaldo). p_centro va null a
// propósito: el desempate Hospitalet/Molins se hace por CP dentro de la función.
const matchReglasSQL = async (lead) => {
  try {
    const resp = await fetch(`${SUPABASE_URL}/rest/v1/rpc/fn_match_reglas`, {
      method: 'POST',
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        p_marca: lead?.marca || null,
        p_origen: lead?.origen || null,
        p_tipo_venta: lead?.tipo_venta || null,
        p_tipo_cliente: lead?.tipo_cliente || null,
        p_razon_social: lead?.razon_social || null,
        p_centro: null,
        p_cp: lead?.cp || null,
      }),
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!Array.isArray(data)) return null;
    return data.map(x => Number((x && typeof x === 'object') ? (x.fn_match_reglas ?? Object.values(x)[0]) : x)).filter(n => !Number.isNaN(n));
  } catch (e) { return null; }
};

// ==================== INBOX VIEW ====================
const InboxView = ({ inboxLeads, comerciales, reglas, comercialesStats, onAssign, onReject, onDelete, onRefresh, selectedLead, setSelectedLead }) => {
  const [filterOrigen, setFilterOrigen] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [autoAssignProgress, setAutoAssignProgress] = useState({ total: 0, done: 0, success: 0 });
  const [assigningLeads, setAssigningLeads] = useState(new Set()); // IDs de leads que se están asignando
  
  const filteredLeads = useMemo(() => {
    return inboxLeads.filter(lead => {
      // Mismo criterio que el badge del sidebar y la UI móvil.
      if (!isPendingInboxLead(lead)) return false;
      if (filterOrigen !== 'all' && lead.origen !== filterOrigen) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        return lead.nombre?.toLowerCase().includes(s) || lead.email?.toLowerCase().includes(s) || lead.telefono?.includes(s);
      }
      return true;
    });
  }, [inboxLeads, filterOrigen, searchTerm]);

  const origenesList = useMemo(() => [...new Set(inboxLeads.map(l => l.origen))].filter(Boolean), [inboxLeads]);

  // Helper para parsear comerciales_pool (puede venir como array o como JSON string)
  const parsePool = (pool) => {
    if (!pool) return [];
    if (Array.isArray(pool)) return pool.map(id => parseInt(id, 10));
    if (typeof pool === 'string') {
      try {
        const parsed = JSON.parse(pool);
        if (Array.isArray(parsed)) return parsed.map(id => parseInt(id, 10));
      } catch (e) {
        console.warn('Error parseando comerciales_pool:', pool);
      }
    }
    return [];
  };

  const selectBestComercial = useCallback((pool, criterio, stats, maxLeadsActivos = 0) => {
    const parsedPool = parsePool(pool);
    if (parsedPool.length === 0) return null;

    const poolStats = parsedPool.map(id => {
      const comercial = comerciales.find(c => c.id === id);
      const stat = stats.find(s => s.comercial_id === id) || { leads_activos: 0, tiempo_medio_respuesta_horas: 999, tasa_conversion_30d: 0, ultimo_lead_asignado: '1970-01-01' };
      return { comercial, stat };
    }).filter(x => x.comercial && !x.comercial.agenda_bloqueada);
    if (poolStats.length === 0) return null;

    let sorted, razon;
    switch(criterio) {
      case 'menor_carga': sorted = poolStats.sort((a, b) => a.stat.leads_activos - b.stat.leads_activos); razon = `${sorted[0].stat.leads_activos} leads activos`; break;
      case 'mas_rapido': sorted = poolStats.sort((a, b) => (a.stat.tiempo_medio_respuesta_horas || 999) - (b.stat.tiempo_medio_respuesta_horas || 999)); razon = `${(sorted[0].stat.tiempo_medio_respuesta_horas || 0).toFixed(1)}h respuesta`; break;
      case 'mejor_conversion': sorted = poolStats.sort((a, b) => (b.stat.tasa_conversion_30d || 0) - (a.stat.tasa_conversion_30d || 0)); razon = `${((sorted[0].stat.tasa_conversion_30d || 0) * 100).toFixed(0)}% conversión`; break;
      case 'round_robin': sorted = poolStats.sort((a, b) => new Date(a.stat.ultimo_lead_asignado || 0) - new Date(b.stat.ultimo_lead_asignado || 0)); razon = 'Rotación'; break;
      case 'score_combinado': sorted = poolStats.map(p => ({...p, score: (100 - p.stat.leads_activos) * 0.4 + (100 - Math.min(p.stat.tiempo_medio_respuesta_horas || 100, 100)) * 0.3 + ((p.stat.tasa_conversion_30d || 0) * 100) * 0.3})).sort((a, b) => b.score - a.score); razon = `Score: ${sorted[0].score.toFixed(0)}`; break;
      // PATCH 2026-05-11.F: paridad con Edge Function v10 — respeta orden de pool y devuelve
      // el primero con leads_activos < max_leads_activos (si está definido en la regla).
      // Si todos saturados, vuelve al primero del pool (no se pierde el lead).
      case 'preferente_pool': {
        if (maxLeadsActivos > 0) {
          const disp = poolStats.find(p => (p.stat.leads_activos || 0) < maxLeadsActivos);
          if (disp) return { comercial: disp.comercial, razon: `Preferente (${disp.stat.leads_activos}/${maxLeadsActivos})` };
        }
        return { comercial: poolStats[0].comercial, razon: `Preferente (primero del pool)` };
      }
      default: sorted = poolStats.sort((a, b) => a.stat.leads_activos - b.stat.leads_activos); razon = `${sorted[0].stat.leads_activos} activos`;
    }
    return { comercial: sorted[0].comercial, razon };
  }, [comerciales]);

  const findMatchLocal = useCallback((lead) => {
    if (!reglas?.length) return null;
    // PATCH 2026-05-07: el matcheador ahora considera B2B cuando el lead viene
    // marcado como Empresa/B2B/Flota o tiene razon_social. Antes comparaba
    // tipo_venta como string literal y las reglas 'Flotas B2B'/'B2B' nunca
    // matcheaban con leads que entran como tipo_venta='VN'+tipo_cliente='Empresa'.
    // Loli Moral llevaba semanas sin recibir B2B porque sus 8 reglas no disparaban.
    const isLeadB2B = (
      (lead.tipo_cliente && /^(empresa|b2b|flota)/i.test(String(lead.tipo_cliente)))
      || (lead.razon_social && String(lead.razon_social).trim() !== '')
    );
    const effLeadTV = isLeadB2B ? 'B2B' : (lead.tipo_venta || '');
    const normReglaTV = (rtv) => {
      if (!rtv) return '';
      const s = String(rtv).toUpperCase();
      if (s.includes('B2B') || s.includes('FLOTA')) return 'B2B';
      return s.trim();
    };
    for (const r of [...reglas].sort((a, b) => (a.prioridad || 99) - (b.prioridad || 99))) {
      if (r.activo === false) continue;
      let ok = true;
      if (r.marca && lead.marca && r.marca !== lead.marca) ok = false;
      if (r.origen && lead.origen && r.origen !== lead.origen) ok = false;
      const reglaTV = normReglaTV(r.tipo_venta);
      if (reglaTV && effLeadTV && reglaTV !== effLeadTV) ok = false;
      if (r.centro && lead.centro && r.centro !== lead.centro) ok = false;
      if (ok) {
        const parsedPool = parsePool(r.comerciales_pool);
        if (parsedPool.length > 0) {
          const result = selectBestComercial(r.comerciales_pool, r.criterio_seleccion || 'score_combinado', comercialesStats, r.max_leads_activos || 0);
          if (result) return { comercial: result.comercial, regla: r, razon: result.razon };
        } else if (r.comercial_id) {
          const c = comerciales.find(x => x.id === r.comercial_id);
          if (c) return { comercial: c, regla: r, razon: 'Asignación directa' };
        }
      }
    }
    return null;
  }, [reglas, comerciales, comercialesStats, selectBestComercial]);

  // ── Matcher SQL (un solo cerebro): fn_match_reglas ────────────────────
  // PATCH 2026-06-22: la fuente de verdad del MATCH de regla es la función SQL
  // public.fn_match_reglas (misma que usan la Edge Function y el backend c360):
  // contempla diéresis (Citroën), desempate Hospitalet/Molins por CP e inferencia
  // B2B por tipo_cliente/razón social. La selección del comercial dentro del pool
  // sigue en selectBestComercial (igual que al asignar). Si la RPC falla, findMatch
  // cae a findMatchLocal -> nunca rompe el flujo actual.
  const buildMatchFromReglaIds = useCallback((lead, reglaIds) => {
    if (!Array.isArray(reglaIds) || !reglas?.length) return null;
    for (const rid of reglaIds) {
      const r = reglas.find(x => Number(x.id) === Number(rid) && x.activo !== false);
      if (!r) continue;
      const parsedPool = parsePool(r.comerciales_pool);
      if (parsedPool.length > 0) {
        const result = selectBestComercial(r.comerciales_pool, r.criterio_seleccion || 'score_combinado', comercialesStats, r.max_leads_activos || 0);
        if (result) return { comercial: result.comercial, regla: r, razon: result.razon };
      } else if (r.comercial_id) {
        const c = comerciales.find(x => x.id === r.comercial_id);
        if (c) return { comercial: c, regla: r, razon: 'Asignación directa' };
      }
    }
    return null;
  }, [reglas, comerciales, comercialesStats, selectBestComercial]);

  const [sqlMatchMap, setSqlMatchMap] = useState({});
  useEffect(() => {
    let cancelado = false;
    (async () => {
      if (!filteredLeads?.length || !reglas?.length) { setSqlMatchMap({}); return; }
      const pares = await Promise.all(filteredLeads.map(async (lead) => {
        const ids = await matchReglasSQL(lead);
        if (ids === null) return [lead.id, undefined];
        return [lead.id, buildMatchFromReglaIds(lead, ids)];
      }));
      if (cancelado) return;
      const map = {};
      for (const [id, m] of pares) { if (m !== undefined) map[id] = m; }
      setSqlMatchMap(map);
    })();
    return () => { cancelado = true; };
  }, [filteredLeads, reglas, comercialesStats, comerciales, buildMatchFromReglaIds]);

  const findMatch = useCallback((lead) => {
    if (lead && Object.prototype.hasOwnProperty.call(sqlMatchMap, lead.id)) return sqlMatchMap[lead.id];
    return findMatchLocal(lead);
  }, [sqlMatchMap, findMatchLocal]);

  // Función mejorada de auto-asignación con progreso
  const handleBulkAssign = async () => {
    const leadsToAssign = filteredLeads.filter(lead => findMatch(lead));
    if (leadsToAssign.length === 0) {
      alert('No hay leads con reglas coincidentes para asignar');
      return;
    }
    
    const confirmMsg = `¿Asignar ${leadsToAssign.length} leads automáticamente?\n\nSe enviará email de notificación a cada comercial.`;
    if (!window.confirm(confirmMsg)) return;
    
    setIsAutoAssigning(true);
    setAutoAssignProgress({ total: leadsToAssign.length, done: 0, success: 0 });
    
    let success = 0;
    for (let i = 0; i < leadsToAssign.length; i++) {
      const lead = leadsToAssign[i];
      const match = findMatch(lead);
      if (match) {
        try {
          await onAssign(lead, match.comercial, true); // true = enviar email
          success++;
        } catch (e) {
          console.error('Error asignando lead:', e);
        }
      }
      setAutoAssignProgress({ total: leadsToAssign.length, done: i + 1, success });
    }
    
    setIsAutoAssigning(false);
    setAutoAssignProgress({ total: 0, done: 0, success: 0 });
    onRefresh();
    alert(`✅ Completado: ${success}/${leadsToAssign.length} leads asignados`);
  };

  // Contar leads que tienen match
  const leadsWithMatch = useMemo(() => filteredLeads.filter(lead => findMatch(lead)).length, [filteredLeads, findMatch]);

  const handleQuickAssign = async (lead, comercialId) => {
    // Prevenir asignaciones duplicadas en proceso
    if (assigningLeads.has(lead.id)) {
      return;
    }
    
    // Marcar como "asignando" INMEDIATAMENTE
    setAssigningLeads(prev => new Set([...prev, lead.id]));
    
    try {
      // VERIFICACIÓN FRESH: Recargar el lead desde la BD
      const freshLead = await fetchInboxLeadById(lead.id);
      
      if (!freshLead) {
        alert('El lead ya no existe');
        await onRefresh();
        return;
      }
      
      if (freshLead.comercial_asignado) {
        alert(`Este lead ya fue asignado a ${freshLead.comercial_asignado}`);
        await onRefresh();
        return;
      }
      
      if (freshLead.estado === 'asignado' || freshLead.estado === 'rechazado') {
        alert(`Este lead ya tiene estado: ${freshLead.estado}`);
        await onRefresh();
        return;
      }
      
      const comercial = comerciales.find(c => c.id === parseInt(comercialId));
      if (!comercial) {
        alert('Comercial no encontrado');
        return;
      }
      
      // Asignar con el lead FRESH
      await onAssign(freshLead, comercial, true);
    } catch (error) {
      console.error('Error asignando lead:', error);
      alert('Error al asignar el lead. Por favor intenta de nuevo.');
    } finally {
      // Limpiar el estado después de asignar
      setAssigningLeads(prev => {
        const newSet = new Set(prev);
        newSet.delete(lead.id);
        return newSet;
      });
    }
  };

  return (
    <div style={{padding: '16px 20px'}}>
      <SectionTitle>Leads pendientes</SectionTitle>
      <div style={{display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap'}}>
        <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{...campo, padding: '7px 12px', width: '180px'}} />
        <select value={filterOrigen} onChange={(e) => setFilterOrigen(e.target.value)} style={{...campo, padding: '7px 12px'}}>
          <option value="all">Todos los orígenes</option>
          {origenesList.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <button
          onClick={handleBulkAssign}
          disabled={isAutoAssigning || leadsWithMatch === 0}
          style={{
            ...botonPrimario,
            padding: '8px 16px',
            background: isAutoAssigning ? C.faint : leadsWithMatch > 0 ? C.verde : C.line,
            color: leadsWithMatch > 0 ? '#fff' : C.faint,
            boxShadow: leadsWithMatch > 0 && !isAutoAssigning ? '0 3px 10px rgba(46,158,91,.28)' : 'none',
            cursor: leadsWithMatch > 0 && !isAutoAssigning ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          {isAutoAssigning ? (
            <>⏳ {autoAssignProgress.done}/{autoAssignProgress.total}</>
          ) : (
            <>🧠 Auto-asignar {leadsWithMatch > 0 && `(${leadsWithMatch})`}</>
          )}
        </button>
        <div style={{marginLeft: 'auto', fontSize: '12.5px', color: C.muted, display: 'flex', alignItems: 'center'}}>
          {filteredLeads.length} pendientes {leadsWithMatch > 0 && <span style={{color: C.verde, fontWeight: 700, marginLeft: '8px'}}>• {leadsWithMatch} con regla</span>}
        </div>
      </div>
      <div style={{...tarjeta, overflow: 'hidden'}}>
        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12.5px'}}>
          <thead>
            <tr>
              <th style={celdaTitulo}>Cliente</th>
              <th style={celdaTitulo}>Origen</th>
              <th style={celdaTitulo}>Vehículo</th>
              <th style={celdaTitulo}>Tipo</th>
              <th style={celdaTitulo}>Match</th>
              <th style={celdaTitulo}>Asignar a</th>
              <th style={celdaTitulo}>Fecha</th>
              <th style={{...celdaTitulo, textAlign: 'center', width: '40px'}}></th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map(lead => {
              const match = findMatch(lead);
              const isAssigning = assigningLeads.has(lead.id);
              return (
                <tr key={lead.id} style={{borderBottom: SEPARADOR_FILA, opacity: isAssigning ? 0.5 : 1}}>
                  <td style={{padding: '9px 12px'}}>
                    <div style={{fontWeight: 700, fontSize: '12.5px', color: C.ink}}>{lead.nombre || 'Sin nombre'} {lead.apellidos || ''}</div>
                    <div style={{fontSize: '11px', color: C.muted}}>{lead.email || lead.telefono || lead.movil}</div>
                  </td>
                  <td style={{padding: '9px 12px'}}>
                    <span style={pildora(getOrigenColor(lead.origen))}>{lead.origen}</span>
                  </td>
                  <td style={{padding: '9px 12px'}}>
                    <div style={{fontWeight: 700, fontSize: '12.5px', color: C.ink}}>{lead.marca || '?'}</div>
                    <div style={{fontSize: '11px', color: C.muted}}>{lead.modelo || '-'}</div>
                  </td>
                  <td style={{padding: '9px 12px'}}>
                    {lead.tipo_venta && <span style={pildora(lead.tipo_venta === 'VN' ? C.brand : lead.tipo_venta === 'B2B' ? C.verde : C.naranja)}>{lead.tipo_venta}</span>}
                  </td>
                  <td style={{padding: '9px 12px'}}>
                    {match ? (
                      <span style={pildora(C.verde)}>
                        🧠 {match.comercial.nombre}
                      </span>
                    ) : (
                      <span style={{fontSize: '11px', color: C.faint}}>Sin regla</span>
                    )}
                  </td>
                  <td style={{padding: '9px 12px'}}>
                    <select 
                      value=""
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                          handleQuickAssign(lead, value);
                          e.target.value = ''; // Reset selector
                        }
                      }}
                      disabled={isAssigning}
                      style={{
                        ...campo,
                        padding: '6px 9px',
                        fontSize: '12px',
                        cursor: isAssigning ? 'not-allowed' : 'pointer',
                        minWidth: '130px',
                        opacity: isAssigning ? 0.6 : 1
                      }}
                    >
                      <option value="">{isAssigning ? 'Asignando...' : 'Seleccionar...'}</option>
                      {comerciales.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </td>
                  <td style={{padding: '9px 12px', fontSize: '12px', color: C.muted}}>
                    {formatRelativeTime(lead.fecha_recepcion)}
                  </td>
                  <td style={{padding: '9px 12px', textAlign: 'center'}}>
                    <button
                      onClick={() => onDelete && onDelete(lead)}
                      disabled={isAssigning}
                      title="Eliminar lead"
                      style={{
                        ...botonSecundario,
                        padding: '5px 9px',
                        color: C.rojo,
                        fontSize: '12px',
                        cursor: isAssigning ? 'not-allowed' : 'pointer',
                        opacity: isAssigning ? 0.5 : 1,
                        lineHeight: 1
                      }}
                    >🗑️</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredLeads.length === 0 && (
          <EmptyState icon="📭" title="No hay leads pendientes" />
        )}
      </div>
    </div>
  );
};

// ==================== ASSIGNED LEADS VIEW ====================
const AssignedLeadsView = ({ leads, inboxLeads, onDelete }) => {
  const [search, setSearch] = useState('');
  const [filterRegistro, setFilterRegistro] = useState('all');
  
  // Crear lista combinada: leads de inbox asignados con verificación si están en tabla leads
  const assignedFromInbox = useMemo(() => {
    return inboxLeads
      .filter(inbox => inbox.comercial_asignado) // Solo los asignados
      .map(inbox => {
        // Verificar si este lead realmente está en la tabla leads.
        // PATCH 2026-04-30: el match anterior usaba (nombre,email,telefono) y
        // fallaba en dos casos:
        //   1) inbox.nombre = 'Sin nombre' (n8n inserta antes de extraer JSON)
        //      -> el lead en `leads` tenía nombre real -> no matcheaba nunca.
        //   2) Race realtime: inbox INSERT llegaba antes que la subscripción
        //      a `leads` -> el lead aparecía como "duplicado" hasta F5.
        // Ahora matcheamos por foreign_id (canal único) y por
        // email+telefono+marca, ignorando nombre.
        const sameMarca = (a, b) => {
          if (!a || !b) return true; // si una no tiene marca, no descartar
          return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
        };
        const existeEnLeads = leads.some(lead =>
          // 1) Legacy: lead_id `lead-inbox-X-...`
          (lead.lead_id && lead.lead_id.includes(`inbox-${inbox.id}`)) ||
          // 2) Match por foreign_id (Carnovo, StockSpark, etc.) + marca
          (inbox.foreign_id && lead.foreign_id &&
           String(lead.foreign_id) === String(inbox.foreign_id) &&
           sameMarca(lead.marca, inbox.marca)) ||
          // 3) Match por datos cliente: email + telefono + marca (sin nombre)
          (lead.email && inbox.email && lead.email === inbox.email &&
           lead.telefono === inbox.telefono &&
           sameMarca(lead.marca, inbox.marca))
        );
        
        return {
          ...inbox,
          registrado: existeEnLeads,
          tipo_registro: existeEnLeads ? 'registrado' : 'duplicado'
        };
      })
      .sort((a, b) => new Date(b.fecha_asignacion || 0) - new Date(a.fecha_asignacion || 0));
  }, [inboxLeads, leads]);
  
  const filtered = useMemo(() => {
    return assignedFromInbox.filter(l => {
      // Filtro por tipo de registro
      if (filterRegistro === 'registrados' && !l.registrado) return false;
      if (filterRegistro === 'duplicados' && l.registrado) return false;
      
      // Filtro por búsqueda
      if (search) {
        const s = search.toLowerCase();
        return l.nombre?.toLowerCase().includes(s) || 
               l.email?.toLowerCase().includes(s) ||
               l.comercial_asignado?.toLowerCase().includes(s);
      }
      return true;
    });
  }, [assignedFromInbox, search, filterRegistro]);
  
  const stats = useMemo(() => ({
    total: assignedFromInbox.length,
    registrados: assignedFromInbox.filter(l => l.registrado).length,
    duplicados: assignedFromInbox.filter(l => !l.registrado).length
  }), [assignedFromInbox]);
  
  return (
    <div style={{padding: '16px 20px'}}>
      <SectionTitle>Leads asignados</SectionTitle>

      {/* Filtros y estadísticas */}
      <div style={{display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap'}}>
        <input
          type="text"
          placeholder="🔍 Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{...campo, width: '200px'}}
        />

        <select
          value={filterRegistro}
          onChange={(e) => setFilterRegistro(e.target.value)}
          style={campo}
        >
          <option value="all">Todos ({stats.total})</option>
          <option value="registrados">✅ Registrados ({stats.registrados})</option>
          <option value="duplicados">⚠️ Duplicados ({stats.duplicados})</option>
        </select>

        {/* Badges de resumen */}
        <div style={{marginLeft: 'auto', display: 'flex', gap: '8px'}}>
          <span style={pildora(C.verde)}>
            ✅ {stats.registrados} en sistema
          </span>
          {stats.duplicados > 0 && (
            <span style={pildora(C.naranja)}>
              ⚠️ {stats.duplicados} duplicados
            </span>
          )}
        </div>
      </div>

      <div style={{...tarjeta, overflow: 'hidden'}}>
        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12.5px'}}>
          <thead>
            <tr>
              <th style={celdaTitulo}>Estado Registro</th>
              <th style={celdaTitulo}>Cliente</th>
              <th style={celdaTitulo}>Origen</th>
              <th style={celdaTitulo}>Vehículo</th>
              <th style={celdaTitulo}>Comercial</th>
              <th style={celdaTitulo}>Fecha Asignación</th>
              <th style={{...celdaTitulo, textAlign: 'center', width: '50px'}}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => (
              <tr
                key={l.id}
                style={{
                  borderBottom: SEPARADOR_FILA,
                  background: l.registrado ? 'white' : '#fdf8ef'
                }}
              >
                {/* Estado de Registro */}
                <td style={{padding: '10px 14px'}}>
                  {l.registrado ? (
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                      <span style={pildora(C.verde)}>
                        ✅ Registrado
                      </span>
                    </div>
                  ) : (
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                      <span style={pildora(C.naranja)}>
                        ⚠️ Duplicado
                      </span>
                      <span style={{fontSize: '11px', color: C.faint}}>
                        (Ya existía)
                      </span>
                    </div>
                  )}
                </td>

                {/* Cliente */}
                <td style={{padding: '10px 14px'}}>
                  <div style={{fontWeight: 700, color: C.ink}}>{l.nombre || 'Sin nombre'} {l.apellidos || ''}</div>
                  <div style={{fontSize: '11px', color: C.muted}}>{l.email || l.telefono || l.movil}</div>
                </td>

                {/* Origen */}
                <td style={{padding: '10px 14px'}}>
                  <span style={pildora(getOrigenColor(l.origen))}>
                    {l.origen}
                  </span>
                </td>

                {/* Vehículo */}
                <td style={{padding: '10px 14px'}}>
                  <div style={{fontWeight: 700, color: C.ink}}>{l.marca || '?'}</div>
                  <div style={{fontSize: '11px', color: C.muted}}>{l.modelo || '-'}</div>
                </td>

                {/* Comercial */}
                <td style={{padding: '10px 14px', fontWeight: 700, color: C.page}}>
                  {l.comercial_asignado || '-'}
                </td>

                {/* Fecha */}
                <td style={{padding: '10px 14px', fontSize: '12px', color: C.muted}}>
                  {l.fecha_asignacion ? formatDateTime(l.fecha_asignacion) : '-'}
                </td>

                {/* Acción: eliminar */}
                <td style={{padding: '10px 14px', textAlign: 'center'}}>
                  <button
                    onClick={() => onDelete && onDelete(l)}
                    title="Eliminar lead"
                    style={{
                      ...botonSecundario,
                      padding: '5px 9px',
                      color: C.rojo,
                      fontSize: '13px',
                      lineHeight: 1
                    }}
                  >🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filtered.length === 0 && (
          <EmptyState icon="📋" title="No hay leads que mostrar" />
        )}
      </div>

      {/* Leyenda explicativa */}
      {stats.duplicados > 0 && (
        <div style={{marginTop: '16px', padding: '12px 16px', background: `${C.naranja}14`, border: `1px solid ${C.naranja}44`, borderRadius: 12, fontSize: '12.5px', color: C.page, lineHeight: 1.55}}>
          <strong>ℹ️ Sobre duplicados:</strong> Estos leads ya existían en el sistema (mismo nombre, email y teléfono). 
          Se marcaron como asignados pero NO se creó un registro duplicado en la base de datos. 
          El lead original se encuentra en la tabla "leads" bajo otro comercial.
        </div>
      )}
    </div>
  );
};

// ==================== COMERCIALES VIEW ====================
const ComercialesView = ({ leads, comerciales, comercialesStats }) => {
  const stats = useMemo(() => comerciales.map(c => {
    const cLeads = leads.filter(l => l.vendedor_nombre === c.nombre);
    const stat = comercialesStats.find(s => s.comercial_id === c.id) || {};
    return { ...c, totalLeads: cLeads.length, leadsActivos: stat.leads_activos || cLeads.filter(l => !['Vendido', 'Cancelada', 'Cancelada Comercial'].includes(l.estado_cita)).length, vendidos: cLeads.filter(l => l.estado_cita === 'Vendido').length, pendientes: cLeads.filter(l => l.estado_cita === 'Pendiente').length, tiempoMedio: stat.tiempo_medio_respuesta_horas || 0, conversion: stat.tasa_conversion_30d || 0, ultimoLead: stat.ultimo_lead_asignado };
  }).sort((a, b) => b.leadsActivos - a.leadsActivos), [comerciales, leads, comercialesStats]);

  return (
    <div style={{padding: '16px 20px'}}>
      <SectionTitle>Equipo comercial</SectionTitle>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px'}}>
        {stats.map(c => (
          <div key={c.id} className="fg-card" style={{...tarjeta, padding: '16px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px'}}>
              <div><div style={{fontSize: '14px', fontWeight: 800, color: C.ink}}>{c.nombre}</div><div style={{fontSize: '12px', color: C.muted}}>{c.centro}</div></div>
              <div style={pildora(c.leadsActivos > 15 ? C.rojo : C.verde)}>{c.leadsActivos} activos</div>
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px'}}>
              <div style={{textAlign: 'center'}}><div style={{fontSize: '20px', fontWeight: 800, color: C.brand}}>{c.totalLeads}</div><div style={etiquetaCifra}>Total</div></div>
              <div style={{textAlign: 'center'}}><div style={{fontSize: '20px', fontWeight: 800, color: C.naranja}}>{c.pendientes}</div><div style={etiquetaCifra}>Pendientes</div></div>
              <div style={{textAlign: 'center'}}><div style={{fontSize: '20px', fontWeight: 800, color: C.verde}}>{c.vendidos}</div><div style={etiquetaCifra}>Vendidos</div></div>
              <div style={{textAlign: 'center'}}><div style={{fontSize: '20px', fontWeight: 800, color: C.morado}}>{(c.conversion * 100).toFixed(0)}%</div><div style={etiquetaCifra}>Conversión</div></div>
            </div>
            <div style={{padding: '10px', background: C.bg, borderRadius: 10, fontSize: '11.5px', color: C.muted}}>
              <div style={{display: 'flex', justifyContent: 'space-between'}}><span>⚡ Tiempo respuesta:</span><span style={{fontWeight: 800, color: c.tiempoMedio < 2 ? C.verde : c.tiempoMedio < 8 ? C.naranja : C.rojo}}>{c.tiempoMedio.toFixed(1)}h</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== REGLAS VIEW ====================
const ReglasView = ({ reglas, comerciales, comercialesStats, proximasAsignaciones = [], leads = [], onAddRegla, onDeleteRegla, onUpdateRegla }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingRegla, setEditingRegla] = useState(null);
  const [newRegla, setNewRegla] = useState({ nombre: '', prioridad: 1, marca: '', tipo_venta: '', origen: '', centro: '', comerciales_pool: [], criterio_seleccion: 'menor_carga', max_leads_activos: null, activo: true });

  // Map regla_id → último lead asignado POR LA APP para esa regla.
  // Solo cuenta leads creados por canales automáticos de AutoLeads (no manuales).
  // Matchea por marca (sin acentos/case) + tipo_venta (con tolerancia B2B/Flotas B2B) + comercial en pool.
  const ultimosLeadsPorRegla = useMemo(() => {
    const map = new Map();
    const USUARIOS_APP = ['c360-backend', 'AutoAssign-EdgeFn'];
    // Quita acentos: "Citroën" → "citroen". ̀-ͯ = combining diacriticals.
    const norm = (s) => (s || '').normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '').toLowerCase().trim();
    const matchMarca = (lm, rm) => !rm || norm(lm) === norm(rm);
    const matchTipo = (lt, rt) => {
      if (!rt) return true;
      const rT = rt.toUpperCase(), lT = (lt || '').toUpperCase();
      return rT === lT || (lT === 'B2B' && (rT.includes('B2B') || rT.includes('FLOTA')));
    };
    reglas.forEach(r => {
      const pool = (r.comerciales_pool || []).map(id => comerciales.find(c => c.id === id)?.nombre).filter(Boolean);
      if (!pool.length && r.comercial_id) { const c = comerciales.find(x => x.id === r.comercial_id); if (c) pool.push(c.nombre); }
      if (!pool.length) { map.set(r.id, null); return; }
      const last = leads
        .filter(l => USUARIOS_APP.includes(l.usuario_alta) && l.vendedor_nombre && pool.includes(l.vendedor_nombre) && matchMarca(l.marca, r.marca) && matchTipo(l.tipo_venta, r.tipo_venta))
        .sort((a, b) => new Date(b.fecha_creacion || 0) - new Date(a.fecha_creacion || 0))[0];
      map.set(r.id, last || null);
    });
    return map;
  }, [leads, reglas, comerciales]);

  const resetForm = () => { setNewRegla({ nombre: '', prioridad: 1, marca: '', tipo_venta: '', origen: '', centro: '', comerciales_pool: [], criterio_seleccion: 'menor_carga', max_leads_activos: null, activo: true }); setEditingRegla(null); };

  const handleAdd = async () => {
    const reglaToSave = { ...newRegla, comerciales_pool: newRegla.comerciales_pool.length > 0 ? newRegla.comerciales_pool : null, comercial_id: newRegla.comerciales_pool.length === 1 ? newRegla.comerciales_pool[0] : null };
    if (editingRegla) await onUpdateRegla(editingRegla.id, reglaToSave); else await onAddRegla(reglaToSave);
    setShowModal(false); resetForm();
  };

  const handleEdit = (regla) => {
    setEditingRegla(regla);
    setNewRegla({ nombre: regla.nombre || '', prioridad: regla.prioridad || 1, marca: regla.marca || '', tipo_venta: regla.tipo_venta || '', origen: regla.origen || '', centro: regla.centro || '', comerciales_pool: regla.comerciales_pool || (regla.comercial_id ? [regla.comercial_id] : []), criterio_seleccion: regla.criterio_seleccion || 'menor_carga', max_leads_activos: regla.max_leads_activos || null, activo: regla.activo !== false });
    setShowModal(true);
  };

  return (
    <div style={{padding: '16px 20px'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap', marginBottom: '16px'}}>
        <div><h2 style={{fontSize: '14px', fontWeight: 800, color: C.ink, margin: 0, letterSpacing: 0.2}}>Reglas de asignación inteligente</h2><p style={{fontSize: '12.5px', color: C.muted, margin: '4px 0 0'}}>Pool de comerciales + criterio de selección</p></div>
        <button onClick={() => { resetForm(); setShowModal(true); }} style={botonPrimario}>➕ Nueva</button>
      </div>
      <div style={{background: `${C.brand}0d`, border: `1px solid ${C.brand}33`, borderRadius: 12, padding: '14px 16px', marginBottom: '20px'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px'}}><span style={{fontSize: '16px'}}>🧠</span><span style={{fontWeight: 800, fontSize: '12.5px', color: C.brand}}>Asignación inteligente</span></div>
        <p style={{fontSize: '12.5px', color: C.page, margin: 0}}>Selecciona múltiples comerciales y un criterio. El sistema asigna automáticamente al mejor.</p>
      </div>
      <div style={{...tarjeta, overflow: 'hidden'}}>
        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12.5px'}}>
          <thead><tr><th style={{...celdaTitulo, width: '60px'}}>Prior.</th><th style={celdaTitulo}>Nombre</th><th style={celdaTitulo}>Condiciones</th><th style={celdaTitulo}>Pool</th><th style={celdaTitulo}>Criterio</th><th style={{...celdaTitulo, textAlign: 'center', width: '100px'}}>Acciones</th></tr></thead>
          <tbody>{reglas.sort((a, b) => (a.prioridad || 99) - (b.prioridad || 99)).map(r => {
            const conds = []; if (r.marca) conds.push(`${r.marca}`); if (r.origen) conds.push(`${r.origen}`); if (r.tipo_venta) conds.push(`${r.tipo_venta}`); if (r.centro) conds.push(`${r.centro}`);
            const criterioInfo = CRITERIOS_SELECCION.find(c => c.value === r.criterio_seleccion) || CRITERIOS_SELECCION[0];
            const poolNames = (r.comerciales_pool || []).map(id => comerciales.find(c => c.id === id)?.nombre).filter(Boolean);
            const legacyComercial = r.comercial_id && !r.comerciales_pool?.length ? comerciales.find(c => c.id === r.comercial_id) : null;
            return (
              <tr key={r.id} style={{borderBottom: SEPARADOR_FILA, opacity: r.activo === false ? 0.5 : 1}}>
                <td style={{padding: '10px 14px'}}><span style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', background: `${C.brand}14`, color: C.brand, borderRadius: 10, fontWeight: 800}}>{r.prioridad || '-'}</span></td>
                <td style={{padding: '10px 14px'}}><div style={{fontWeight: 700, color: C.ink}}>{r.nombre || 'Sin nombre'}</div></td>
                <td style={{padding: '10px 14px'}}><div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>{conds.length > 0 ? conds.map((c, i) => <span key={i} style={pildora(C.muted)}>{c}</span>) : <span style={{fontSize: '11px', color: C.faint}}>Todos</span>}</div></td>
                <td style={{padding: '10px 14px'}}>{poolNames.length > 0 ? <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>{poolNames.slice(0, 2).map((name, i) => <span key={i} style={pildora(C.brand)}>{name}</span>)}{poolNames.length > 2 && <span style={pildora(C.muted)}>+{poolNames.length - 2}</span>}</div> : legacyComercial ? <span style={pildora(C.naranja)}>{legacyComercial.nombre}</span> : '-'}</td>
                <td style={{padding: '10px 14px'}}><span>{criterioInfo.icon} {criterioInfo.label}</span></td>
                <td style={{padding: '10px 14px', textAlign: 'center'}}><button onClick={() => handleEdit(r)} style={{...botonSecundario, padding: '5px 9px', fontSize: '12px', marginRight: '4px'}}>✏️</button><button onClick={() => onDeleteRegla(r.id)} style={{...botonSecundario, padding: '5px 9px', fontSize: '12px', color: C.rojo}}>🗑️</button></td>
              </tr>
            );
          })}</tbody>
        </table>
        {reglas.length === 0 && <EmptyState icon="⚙️" title="No hay reglas" />}
      </div>

      {/* ==================== PRÓXIMAS ASIGNACIONES ==================== */}
      <div style={{marginTop: '24px'}}>
        <div style={{display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px', flexWrap: 'wrap'}}>
          <h3 style={{fontSize: '14px', fontWeight: 800, color: C.ink, margin: 0, letterSpacing: 0.2}}>🎯 Próximas asignaciones</h3>
          <span style={{fontSize: '12.5px', color: C.muted}}>· quién recibirá el siguiente lead de cada regla</span>
        </div>
        <div style={{...tarjeta, overflow: 'hidden'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12.5px'}}>
            <thead><tr><th style={celdaTitulo}>Regla</th><th style={celdaTitulo}>Próximo comercial</th><th style={celdaTitulo}>Pool</th><th style={celdaTitulo}>Última asignación</th><th style={{...celdaTitulo, width: '180px'}}>Última actualización</th></tr></thead>
            <tbody>{[...proximasAsignaciones].sort((a, b) => (a.regla_nombre || '').localeCompare(b.regla_nombre || '')).map(pa => {
              const regla = reglas.find(r => r.id === pa.regla_id);
              const pool = (regla?.comerciales_pool || []).map(id => comerciales.find(c => c.id === id)?.nombre).filter(Boolean);
              const enPool = !regla || (regla.comerciales_pool || []).includes(pa.comercial_id) || regla.comercial_id === pa.comercial_id;
              const updated = pa.updated_at ? new Date(pa.updated_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
              return (
                <tr key={pa.id} style={{borderBottom: SEPARADOR_FILA}}>
                  <td style={{padding: '10px 14px', fontWeight: 700, color: C.ink}}>{pa.regla_nombre || `Regla #${pa.regla_id}`}</td>
                  <td style={{padding: '10px 14px'}}>
                    <span style={pildora(enPool ? C.verde : C.rojo)}>
                      🧠 {pa.comercial_nombre || '—'}
                    </span>
                    {!enPool && <span style={{marginLeft: '8px', fontSize: '11px', color: C.rojo}}>fuera del pool</span>}
                  </td>
                  <td style={{padding: '10px 14px'}}><div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>{pool.length > 0 ? pool.map((n, i) => <span key={i} style={pildora(C.brand)}>{n}</span>) : <span style={{fontSize: '11px', color: C.faint}}>-</span>}</div></td>
                  <td style={{padding: '10px 14px', fontSize: '12px'}}>{(() => {
                    const ul = ultimosLeadsPorRegla.get(pa.regla_id);
                    if (!ul) return <span style={{color: C.faint}}>-</span>;
                    const nombre = `${ul.nombre || ''} ${ul.apellidos || ''}`.trim() || ul.razon_social || '-';
                    return (<div><div style={{fontWeight: 700, color: C.page}}>{nombre}</div><div style={{fontSize: '11px', color: C.muted}}>→ {ul.vendedor_nombre || '?'}</div></div>);
                  })()}</td>
                  <td style={{padding: '10px 14px', fontSize: '12px', color: C.muted}}>{updated}</td>
                </tr>
              );
            })}</tbody>
          </table>
          {proximasAsignaciones.length === 0 && <EmptyState icon="🎯" title="Sin próximas asignaciones cacheadas" />}
        </div>
      </div>

      {showModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
          <div style={{background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, width: '560px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(15,42,70,.24)'}}>
            <div style={{padding: '18px 20px', borderBottom: `1px solid ${C.line}`}}><h3 style={{margin: 0, fontSize: '15px', fontWeight: 800, color: C.ink}}>{editingRegla ? 'Editar regla' : 'Nueva regla'}</h3></div>
            <div style={{padding: '20px'}}>
              <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '16px'}}>
                <div><label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '6px'}}>Nombre</label><input type="text" value={newRegla.nombre} onChange={(e) => setNewRegla({...newRegla, nombre: e.target.value})} placeholder="Ej: Peugeot VN" style={{...campo, width: '100%', padding: '10px 12px'}} /></div>
                <div><label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '6px'}}>Prioridad</label><input type="number" min="1" value={newRegla.prioridad} onChange={(e) => setNewRegla({...newRegla, prioridad: parseInt(e.target.value) || 1})} style={{...campo, width: '100%', padding: '10px 12px'}} /></div>
              </div>
              <div style={{background: C.bg, border: `1px solid ${C.line}`, borderRadius: 12, padding: '16px', marginBottom: '16px'}}>
                <label style={{display: 'block', fontSize: '12px', color: C.muted, marginBottom: '12px', fontWeight: 800}}>📋 Condiciones</label>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                  <div><label style={{display: 'block', fontSize: '11px', fontWeight: 700, color: C.muted, marginBottom: '4px'}}>Marca</label><select value={newRegla.marca} onChange={(e) => setNewRegla({...newRegla, marca: e.target.value})} style={{...campo, width: '100%'}}><option value="">Cualquiera</option>{MARCAS.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
                  <div><label style={{display: 'block', fontSize: '11px', fontWeight: 700, color: C.muted, marginBottom: '4px'}}>Tipo</label><select value={newRegla.tipo_venta} onChange={(e) => setNewRegla({...newRegla, tipo_venta: e.target.value})} style={{...campo, width: '100%'}}><option value="">Cualquiera</option>{TIPOS_VENTA.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                  <div><label style={{display: 'block', fontSize: '11px', fontWeight: 700, color: C.muted, marginBottom: '4px'}}>Origen</label><select value={newRegla.origen} onChange={(e) => setNewRegla({...newRegla, origen: e.target.value})} style={{...campo, width: '100%'}}><option value="">Cualquiera</option>{ORIGENES.map(o => <option key={o.nombre} value={o.nombre}>{o.nombre}</option>)}</select></div>
                  <div><label style={{display: 'block', fontSize: '11px', fontWeight: 700, color: C.muted, marginBottom: '4px'}}>Centro</label><select value={newRegla.centro} onChange={(e) => setNewRegla({...newRegla, centro: e.target.value})} style={{...campo, width: '100%'}}><option value="">Cualquiera</option>{CENTROS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                </div>
              </div>
              <div style={{marginBottom: '16px'}}><label style={{display: 'block', fontSize: '12px', color: C.muted, marginBottom: '6px', fontWeight: 800}}>👥 Pool de comerciales *</label><MultiSelectComerciales comerciales={comerciales} selected={newRegla.comerciales_pool} onChange={(pool) => setNewRegla({...newRegla, comerciales_pool: pool})} comercialesStats={comercialesStats} /></div>
              <div style={{marginBottom: '16px'}}><label style={{display: 'block', fontSize: '12px', color: C.muted, marginBottom: '6px', fontWeight: 800}}>🧠 Criterio de selección</label>
                <div style={{display: 'grid', gap: '8px'}}>{CRITERIOS_SELECCION.map(c => (
                  <label key={c.value} className="fg-nav" style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: newRegla.criterio_seleccion === c.value ? `1px solid ${C.brand}` : `1px solid ${C.line}`, borderRadius: 10, cursor: 'pointer', background: newRegla.criterio_seleccion === c.value ? `${C.brand}0d` : '#fff'}}>
                    <input type="radio" name="criterio" checked={newRegla.criterio_seleccion === c.value} onChange={() => setNewRegla({...newRegla, criterio_seleccion: c.value})} style={{display: 'none'}} />
                    <span style={{fontSize: '20px'}}>{c.icon}</span><div><div style={{fontWeight: 800, fontSize: '12.5px', color: C.ink}}>{c.label}</div><div style={{fontSize: '11px', color: C.muted}}>{c.description}</div></div>
                  </label>
                ))}</div>
              </div>
            </div>
            <div style={{padding: '16px 20px', borderTop: `1px solid ${C.line}`, display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button onClick={() => { setShowModal(false); resetForm(); }} style={botonSecundario}>Cancelar</button>
              <button onClick={handleAdd} disabled={newRegla.comerciales_pool.length === 0} style={{...botonPrimario, background: newRegla.comerciales_pool.length > 0 ? C.brand : C.line, color: newRegla.comerciales_pool.length > 0 ? '#fff' : C.faint, boxShadow: newRegla.comerciales_pool.length > 0 ? botonPrimario.boxShadow : 'none', cursor: newRegla.comerciales_pool.length > 0 ? 'pointer' : 'not-allowed'}}>{editingRegla ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ORIGENES VIEW ====================
const OrigenesView = ({ leads, inboxLeads }) => {
  const stats = useMemo(() => { const all = [...leads, ...inboxLeads]; return ORIGENES.map(o => ({ ...o, total: all.filter(l => l.origen === o.nombre).length, asignados: leads.filter(l => l.origen === o.nombre).length, pendientes: inboxLeads.filter(l => l.origen === o.nombre && isPendingInboxLead(l)).length })).filter(o => o.total > 0).sort((a, b) => b.total - a.total); }, [leads, inboxLeads]);
  return (
    <div style={{padding: '16px 20px'}}>
      <SectionTitle>Orígenes</SectionTitle>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px'}}>
        {stats.map(o => (<div key={o.nombre} className="fg-card" style={{...tarjeta, padding: '16px', borderLeft: `4px solid ${o.color}`}}><div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}><div style={{fontSize: '14px', fontWeight: 800, color: C.ink}}>{o.nombre}</div><div style={pildora(o.color)}>{o.total}</div></div><div style={{display: 'flex', gap: '16px'}}><div><div style={{fontSize: '20px', fontWeight: 800, color: C.verde}}>{o.asignados}</div><div style={etiquetaCifra}>Asignados</div></div><div><div style={{fontSize: '20px', fontWeight: 800, color: C.naranja}}>{o.pendientes}</div><div style={etiquetaCifra}>Pendientes</div></div></div></div>))}
      </div>
      {stats.length === 0 && <div style={tarjeta}><EmptyState icon="📊" title="No hay datos" /></div>}
    </div>
  );
};

// ==================== ANIMATED LOGIN BACKGROUND ====================
const AnimatedLoginBackground = forwardRef(({ cardRect }, ref) => {
  const canvasRef = useRef(null); const animationRef = useRef(null); const stateRef = useRef({ nodes: [], clickBoost: 0 });
  useImperativeHandle(ref, () => ({ triggerPulse: () => { stateRef.current.clickBoost = 1; } }));
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return; const ctx = canvas.getContext('2d'); let width, height;
    const numNodes = 80, connectionDistance = 180, nodeSpeed = 0.3;
    const createNodes = () => { const nodes = []; for (let i = 0; i < numNodes; i++) { nodes.push({ x: Math.random() * width, y: Math.random() * height, vx: (Math.random() - 0.5) * nodeSpeed, vy: (Math.random() - 0.5) * nodeSpeed, radius: 2 + Math.random() * 2, baseRadius: 2 + Math.random() * 2 }); } return nodes; };
    const setup = () => { const dpr = Math.min(window.devicePixelRatio || 1, 2); width = window.innerWidth; height = window.innerHeight; canvas.width = width * dpr; canvas.height = height * dpr; canvas.style.width = `${width}px`; canvas.style.height = `${height}px`; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); if (stateRef.current.nodes.length === 0) stateRef.current.nodes = createNodes(); };
    setup(); window.addEventListener('resize', setup);
    const animate = () => {
      const state = stateRef.current; const nodes = state.nodes; if (state.clickBoost > 0) state.clickBoost = Math.max(0, state.clickBoost - 0.015);
      const bgGradient = ctx.createLinearGradient(0, 0, width, height); bgGradient.addColorStop(0, '#0a1628'); bgGradient.addColorStop(0.5, '#0d1f3c'); bgGradient.addColorStop(1, '#081430'); ctx.fillStyle = bgGradient; ctx.fillRect(0, 0, width, height);
      for (let i = 0; i < nodes.length; i++) { const node = nodes[i]; node.x += node.vx; node.y += node.vy; if (node.x < 0) { node.x = 0; node.vx *= -1; } if (node.x > width) { node.x = width; node.vx *= -1; } if (node.y < 0) { node.y = 0; node.vy *= -1; } if (node.y > height) { node.y = height; node.vy *= -1; } node.radius = node.baseRadius * (1 + state.clickBoost * 0.5); }
      ctx.lineWidth = 1; for (let i = 0; i < nodes.length; i++) { for (let j = i + 1; j < nodes.length; j++) { const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y; const distance = Math.sqrt(dx * dx + dy * dy); if (distance < connectionDistance) { const opacity = (1 - distance / connectionDistance) * 0.6; const boostedOpacity = Math.min(1, opacity + state.clickBoost * 0.3); const gradient = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y); gradient.addColorStop(0, `rgba(77, 130, 188, ${boostedOpacity})`); gradient.addColorStop(0.5, `rgba(132, 182, 244, ${boostedOpacity * 0.8})`); gradient.addColorStop(1, `rgba(77, 130, 188, ${boostedOpacity})`); ctx.strokeStyle = gradient; ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y); ctx.stroke(); } } }
      for (let i = 0; i < nodes.length; i++) { const node = nodes[i]; const glowRadius = node.radius * 4; const glowGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowRadius); const glowIntensity = 0.15 + state.clickBoost * 0.2; glowGradient.addColorStop(0, `rgba(132, 182, 244, ${glowIntensity})`); glowGradient.addColorStop(0.5, `rgba(77, 130, 188, ${glowIntensity * 0.3})`); glowGradient.addColorStop(1, 'rgba(77, 130, 188, 0)'); ctx.fillStyle = glowGradient; ctx.beginPath(); ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2); ctx.fill(); const nodeGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.radius); nodeGradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 + state.clickBoost * 0.1})`); nodeGradient.addColorStop(0.3, `rgba(196, 218, 250, ${0.8 + state.clickBoost * 0.1})`); nodeGradient.addColorStop(1, `rgba(77, 130, 188, 0.6)`); ctx.fillStyle = nodeGradient; ctx.beginPath(); ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2); ctx.fill(); }
      if (cardRect) { const cardCenterX = cardRect.x + cardRect.width / 2; const cardCenterY = cardRect.y + cardRect.height / 2; const glowRadius = Math.max(cardRect.width, cardRect.height) * 0.8; const cardGlow = ctx.createRadialGradient(cardCenterX, cardCenterY, 0, cardCenterX, cardCenterY, glowRadius); const intensity = 0.1 + state.clickBoost * 0.2; cardGlow.addColorStop(0, `rgba(0, 81, 135, ${intensity})`); cardGlow.addColorStop(0.5, `rgba(77, 130, 188, ${intensity * 0.3})`); cardGlow.addColorStop(1, 'rgba(0, 81, 135, 0)'); ctx.fillStyle = cardGlow; ctx.fillRect(0, 0, width, height); }
      animationRef.current = requestAnimationFrame(animate);
    };
    stateRef.current.nodes = createNodes(); animationRef.current = requestAnimationFrame(animate);
    return () => { window.removeEventListener('resize', setup); if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [cardRect]);
  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />;
});

// ==================== LOGIN ====================
const LoginScreen = ({ onLogin, isLoading, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [cardRect, setCardRect] = useState(null);
  const cardRef = useRef(null);
  const canvasRef = useRef(null);

  // Se recuerda el CORREO, nunca la contraseña. Si quedó alguna guardada en
  // claro de antes, se borra al pasar por aquí.
  useEffect(() => {
    localStorage.removeItem('autoleads-remember-password');
    const savedEmail = localStorage.getItem('autoleads-remember-email');
    if (localStorage.getItem('autoleads-remember-me') === 'true' && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    const updateCardRect = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setCardRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
      }
    };
    updateCardRect();
    window.addEventListener('resize', updateCardRect);
    const timer = setTimeout(updateCardRect, 100);
    return () => { window.removeEventListener('resize', updateCardRect); clearTimeout(timer); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setSubmitting(true);
    if (canvasRef.current?.triggerPulse) canvasRef.current.triggerPulse();
    
    try {
      const { data: authData, error: authError } = await supabaseAuth.signIn(email, password);
      if (authData && !authError) {
        const authUser = await supabaseAuth.getCurrentUser();
        if (authUser) {
          const admin = await findAdminByAuthId(authUser.id);
          if (admin) {
            // Guardar o limpiar credenciales según checkbox
            if (rememberMe) {
              localStorage.setItem('autoleads-remember-email', email);
              localStorage.setItem('autoleads-remember-me', 'true');
            } else {
              localStorage.removeItem('autoleads-remember-email');
              localStorage.removeItem('autoleads-remember-password');
              localStorage.removeItem('autoleads-remember-me');
            }
            onLogin(admin);
            return;
          }
        }
      }
      setLoginError(authError || 'Email o contraseña incorrectos');
    } catch (err) {
      setLoginError('Error de conexión. Intenta de nuevo.');
    }
    setSubmitting(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    if (!resetEmail) {
      setResetMessage('error:Por favor ingresa tu email');
      return;
    }
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      if (response.ok) {
        setResetMessage('success:✅ Email de recuperación enviado.');
        setTimeout(() => { setShowForgotPassword(false); setResetEmail(''); setResetMessage(''); }, 3000);
      } else {
        setResetMessage('error:No se pudo enviar el email.');
      }
    } catch (err) {
      setResetMessage('error:Error de conexión.');
    }
  };

  const inputStyle = { ...campo, width: '100%', padding: '10px 12px', background: C.bg };
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '5px' };

  // Mientras se comprueba la sesión no se enseña una pantalla en blanco ni una
  // rueda girando: se dibuja el hueco de la tarjeta con el brillo del sistema.
  if (isLoading) return (
    <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.bg, padding: '16px'}}>
      <div style={{...tarjeta, width: '100%', maxWidth: '360px', padding: '28px'}}>
        <div className="fg-shimmer" style={{height: 22, width: '55%', margin: '0 auto 10px'}} />
        <div className="fg-shimmer" style={{height: 12, width: '75%', margin: '0 auto 22px'}} />
        <div className="fg-shimmer" style={{height: 38, marginBottom: 12}} />
        <div className="fg-shimmer" style={{height: 38, marginBottom: 18}} />
        <div className="fg-shimmer" style={{height: 40}} />
      </div>
      <p style={{color: C.muted, marginTop: 14, fontSize: '12.5px'}}>Verificando sesión...</p>
    </div>
  );

  return (
    <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1f2a37', padding: '16px', position: 'relative', overflow: 'hidden'}}>
      <AnimatedLoginBackground ref={canvasRef} cardRect={cardRect} />
      <div ref={cardRef} style={{width: '100%', maxWidth: '360px', background: 'rgba(255, 255, 255, 0.98)', borderRadius: 12, padding: '28px', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', border: `1px solid ${C.line}`, position: 'relative', zIndex: 1, backdropFilter: 'blur(10px)'}}>
        <div style={{textAlign: 'center', marginBottom: '24px'}}>
          <h1 style={{color: C.ink, fontSize: '19px', fontWeight: 800, margin: '0 0 4px 0', letterSpacing: '-0.2px'}}>Auto Leads</h1>
          <p style={{color: C.muted, fontSize: '12.5px', margin: 0}}>🧠 Asignación Inteligente</p>
        </div>
        {error && (<div style={{background: `${C.rojo}14`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '10px 12px', marginBottom: '14px'}}><span style={{color: C.rojo, fontSize: '12.5px'}}>⚠️ {error}</span></div>)}
        {loginError && (<div style={{background: `${C.rojo}14`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '10px 12px', marginBottom: '14px', textAlign: 'center'}}><span style={{color: C.rojo, fontSize: '12.5px'}}>❌ {loginError}</span></div>)}
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '14px'}}><label style={labelStyle}>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="usuario@dimoauto.com" required style={inputStyle} onFocus={(e) => e.target.style.borderColor = C.brand} onBlur={(e) => e.target.style.borderColor = C.line} /></div>
          <div style={{marginBottom: '14px'}}><label style={labelStyle}>Contraseña</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required style={inputStyle} onFocus={(e) => e.target.style.borderColor = C.brand} onBlur={(e) => e.target.style.borderColor = C.line} /></div>
          <div style={{marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '11.5px', color: C.muted}}><input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{cursor: 'pointer', width: '14px', height: '14px'}} />Recordar credenciales</label>
            <button type="button" onClick={() => setShowForgotPassword(true)} style={{background: 'none', border: 'none', color: C.brand, fontSize: '11.5px', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0}}>¿Olvidaste tu contraseña?</button>
          </div>
          <button type="submit" disabled={submitting} style={{...botonPrimario, width: '100%', padding: '11px', background: submitting ? C.faint : GRAD, boxShadow: submitting ? 'none' : botonPrimario.boxShadow, cursor: submitting ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
            {submitting ? (<>⏳ Verificando...</>) : (<>🔐 Iniciar Sesión</>)}
          </button>
        </form>
        <div style={{marginTop: '16px', padding: '8px', background: `${C.verde}14`, border: `1px solid ${C.verde}44`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'}}>
          <span style={{fontSize: '14px'}}>🔒</span>
          <span style={{fontSize: '11px', fontWeight: 700, color: C.verde}}>Conexión segura con autenticación cifrada</span>
        </div>
        {showForgotPassword && (
          <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px'}} onClick={() => setShowForgotPassword(false)}>
            <div style={{background: 'white', border: `1px solid ${C.line}`, borderRadius: 12, padding: '24px', maxWidth: '340px', width: '100%', boxShadow: '0 24px 60px rgba(15,42,70,.24)'}} onClick={(e) => e.stopPropagation()}>
              <h3 style={{margin: '0 0 8px 0', color: C.ink, fontSize: '15px', fontWeight: 800}}>Recuperar Contraseña</h3>
              <p style={{margin: '0 0 14px 0', color: C.muted, fontSize: '12.5px'}}>Te enviaremos un enlace para restablecer tu contraseña.</p>
              <form onSubmit={handleForgotPassword}>
                <div style={{marginBottom: '14px'}}><label style={labelStyle}>Email</label><input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="usuario@dimoauto.com" required style={inputStyle} /></div>
                {resetMessage && (<div style={{background: resetMessage.startsWith('success:') ? `${C.verde}14` : `${C.rojo}14`, border: `1px solid ${resetMessage.startsWith('success:') ? `${C.verde}44` : `${C.rojo}44`}`, borderRadius: 10, padding: '10px', marginBottom: '14px', fontSize: '12.5px', color: resetMessage.startsWith('success:') ? C.verde : C.rojo}}>{resetMessage.replace('success:', '').replace('error:', '')}</div>)}
                <div style={{display: 'flex', gap: '10px'}}>
                  <button type="button" onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetMessage(''); }} style={{...botonSecundario, flex: 1, padding: '10px'}}>Cancelar</button>
                  <button type="submit" style={{...botonPrimario, flex: 1, padding: '10px', background: GRAD}}>Enviar</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CHANGE PASSWORD MODAL ====================
const ChangePasswordModal = ({ onClose, admin }) => {
  const [currentPassword, setCurrentPassword] = useState(''); const [newPassword, setNewPassword] = useState(''); const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false); const [error, setError] = useState(''); const [success, setSuccess] = useState(false);
  const handleChangePassword = async (e) => { e.preventDefault(); setError(''); if (newPassword.length < 6) { setError('La nueva contraseña debe tener al menos 6 caracteres'); return; } if (newPassword !== confirmPassword) { setError('Las contraseñas no coinciden'); return; } setIsLoading(true); try { const { error: verifyError } = await supabaseAuth.signIn(admin.email, currentPassword); if (verifyError) { setError('La contraseña actual es incorrecta'); setIsLoading(false); return; } const token = localStorage.getItem('sb-access-token'); const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, { method: 'PUT', headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ password: newPassword }) }); if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.message || 'Error al cambiar la contraseña'); } setSuccess(true); setTimeout(() => onClose(), 2000); } catch (err) { setError(err.message || 'Error al cambiar la contraseña'); } finally { setIsLoading(false); } };
  return (
    <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
      <div style={{background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, width: '100%', maxWidth: '400px', boxShadow: '0 24px 60px rgba(15,42,70,.24)', overflow: 'hidden'}}>
        <div style={{background: GRAD, padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}><h3 style={{margin: 0, color: 'white', fontSize: '15px', fontWeight: 800}}>🔐 Cambiar Contraseña</h3><button onClick={onClose} style={{background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: 999, cursor: 'pointer', fontSize: '15px'}}>✕</button></div>
        <form onSubmit={handleChangePassword} style={{padding: '24px'}}>
          {success ? (<div style={{background: `${C.verde}14`, border: `1px solid ${C.verde}44`, borderRadius: 12, padding: '20px', textAlign: 'center'}}><span style={{fontSize: '40px', display: 'block', marginBottom: '12px'}}>✅</span><p style={{color: C.verde, fontWeight: 800, fontSize: '13px', margin: 0}}>Contraseña cambiada correctamente</p></div>) : (<>
            <div style={{marginBottom: '16px'}}><label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '6px'}}>Usuario</label><input type="text" value={admin?.email || ''} disabled style={{...campo, width: '100%', padding: '10px 12px', background: C.bg, color: C.muted}} /></div>
            <div style={{marginBottom: '16px'}}><label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '6px'}}>Contraseña actual *</label><input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required style={{...campo, width: '100%', padding: '10px 12px'}} /></div>
            <div style={{marginBottom: '16px'}}><label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '6px'}}>Nueva contraseña *</label><input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} style={{...campo, width: '100%', padding: '10px 12px'}} /><span style={{fontSize: '11px', color: C.muted}}>Mínimo 6 caracteres</span></div>
            <div style={{marginBottom: '20px'}}><label style={{display: 'block', fontSize: '12px', fontWeight: 700, color: C.muted, marginBottom: '6px'}}>Confirmar nueva contraseña *</label><input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required style={{...campo, width: '100%', padding: '10px 12px'}} /></div>
            {error && <div style={{background: `${C.rojo}14`, border: `1px solid ${C.rojo}44`, borderRadius: 10, padding: '12px', marginBottom: '16px', color: C.rojo, fontSize: '12.5px'}}>❌ {error}</div>}
            <div style={{display: 'flex', gap: '12px'}}><button type="button" onClick={onClose} style={{...botonSecundario, flex: 1, padding: '11px'}}>Cancelar</button><button type="submit" disabled={isLoading} style={{...botonPrimario, flex: 1, padding: '11px', background: GRAD, cursor: isLoading ? 'wait' : 'pointer'}}>{isLoading ? 'Guardando...' : 'Cambiar Contraseña'}</button></div>
          </>)}
        </form>
      </div>
    </div>
  );
};

// ==================== VISTA POR MARCA ====================
const VistaPorMarcaView = ({ reglas, comerciales, comercialesStats }) => {
  const [filtroMarca, setFiltroMarca] = useState('all');
  const [colapsadas, setColapsadas] = useState({});
  const parsePool = (pool) => {
    let arr = pool;
    if (typeof pool === 'string') { try { arr = JSON.parse(pool); } catch (e) { arr = []; } }
    if (!Array.isArray(arr)) return [];
    return arr.map(id => Number(id)).filter(n => !isNaN(n));
  };
  const cargaDe = (id) => {
    const s = comercialesStats.find(x => x.comercial_id === id);
    return s ? (s.leads_activos || 0) : 0;
  };
  const comercialDe = (id) => comerciales.find(c => c.id === id) || null;
  const poolInfo = (r) => {
    const ids = parsePool(r.comerciales_pool);
    const finalIds = ids.length ? ids : (r.comercial_id ? [r.comercial_id] : []);
    return finalIds.map(id => {
      const c = comercialDe(id);
      return { id, nombre: c ? c.nombre : `#${id}`, centro: c ? c.centro : '', bloqueada: c ? !!c.agenda_bloqueada : false, carga: cargaDe(id) };
    });
  };
  const estadoRegla = (r) => {
    const pool = poolInfo(r);
    if (pool.length === 0) return { lvl: 'crit', txt: 'Sin pool' };
    const receptores = pool.filter(p => !p.bloqueada);
    if (receptores.length === 0) return { lvl: 'crit', txt: 'Pool bloqueado' };
    if (pool.length > 1 && receptores.length === 1) return { lvl: 'crit', txt: '1 receptor (resto bloqueado)' };
    if (r.criterio_seleccion === 'preferente_pool' && r.max_leads_activos && pool[0] && pool[0].carga > r.max_leads_activos) {
      return { lvl: 'warn', txt: `Titular saturado (>${r.max_leads_activos}) → 2º` };
    }
    if (receptores.length === 1) {
      const carga = receptores[0].carga;
      if (carga > CARGA_CRITICA) return { lvl: 'crit', txt: `Único · carga muy alta (${carga})` };
      if (carga > CARGA_ALTA) return { lvl: 'warn', txt: `Único · carga alta (${carga})` };
      return { lvl: 'neutral', txt: '1 comercial' };
    }
    const cargas = receptores.map(p => p.carga);
    const mx = Math.max(...cargas), mn = Math.min(...cargas);
    if (mx > CARGA_CRITICA) return { lvl: 'warn', txt: `Carga alta (${mx})` };
    const ratio = mn === 0 ? (mx > 20 ? 99 : 1) : mx / mn;
    if (ratio >= 3) return { lvl: 'warn', txt: `Reparto desigual (${mn} vs ${mx})` };
    return { lvl: 'ok', txt: 'Equilibrado' };
  };
  // Un color por nivel: la píldora deriva de él el fondo y el borde.
  const LVL_COLOR = { ok: C.verde, warn: C.naranja, crit: C.rojo, neutral: C.muted };
  const TV_COLOR = { VN: C.brand, VO: C.morado, B2B: C.naranja, '': C.muted };
  const porMarca = useMemo(() => {
    const map = {};
    reglas.forEach(r => { const m = r.marca || 'Sin marca'; (map[m] = map[m] || []).push(r); });
    return map;
  }, [reglas]);
  const marcasVisibles = (filtroMarca === 'all' ? MARCAS_ORDEN : [filtroMarca]).filter(m => porMarca[m]);
  const extras = Object.keys(porMarca).filter(m => !MARCAS_ORDEN.includes(m));
  if (filtroMarca === 'all') extras.forEach(m => marcasVisibles.push(m));
  const peorEstado = (marca) => {
    const activos = (porMarca[marca] || []).filter(r => r.activo !== false);
    let crit = 0, warn = 0;
    activos.forEach(r => { const e = estadoRegla(r); if (e.lvl === 'crit') crit++; else if (e.lvl === 'warn') warn++; });
    return { crit, warn, total: activos.length };
  };
  const kpis = useMemo(() => {
    let crit = 0, warn = 0, activas = 0, inactivas = 0;
    reglas.forEach(r => {
      if (r.activo === false) { inactivas++; return; }
      activas++;
      const e = estadoRegla(r);
      if (e.lvl === 'crit') crit++; else if (e.lvl === 'warn') warn++;
    });
    return { crit, warn, activas, inactivas };
  }, [reglas, comerciales, comercialesStats]);
  const Chip = ({ lvl, children }) => (
    <span style={pildora(LVL_COLOR[lvl] || LVL_COLOR.neutral)}>{children}</span>
  );
  const PoolBars = ({ pool, max }) => {
    const maxCarga = Math.max(...pool.map(p => p.carga), 1);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '230px' }}>
        {pool.map(p => {
          const pct = Math.round(p.carga / maxCarga * 100);
          const color = p.bloqueada ? C.gris : (p.carga > CARGA_CRITICA ? C.rojo : p.carga > 80 ? C.naranja : C.verde);
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span title={`${p.nombre}${p.centro ? ' · ' + p.centro : ''}`} style={{ fontSize: '12px', width: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nombre}</span>
              <span style={{ flex: 1, height: '10px', background: '#eef1f4', borderRadius: 999, overflow: 'hidden', minWidth: '70px' }}>
                <span style={{ display: 'block', height: '100%', width: pct + '%', background: color, borderRadius: 999 }} />
              </span>
              <span style={{ fontSize: '11px', fontWeight: 700, color: C.page, width: '30px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.carga}</span>
              {p.bloqueada && <span style={pildora(C.rojo)}>bloq.</span>}
              {max && p.carga > max && <span style={pildora(C.naranja)}>&gt;max</span>}
            </div>
          );
        })}
      </div>
    );
  };
  // Tarjeta de cifra del sistema (StatCard): mismo dato, forma de la suite.
  const kpiCard = (val, label, color) => (
    <StatCard key={label} label={label} color={color}
      value={typeof val === 'number' ? val : null}
      displayValue={typeof val === 'number' ? undefined : val} />
  );
  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ marginBottom: '18px' }}>
        <h2 style={{ fontSize: '14px', fontWeight: 800, color: C.ink, margin: 0, letterSpacing: 0.2 }}>Vista por marca</h2>
        <p style={{ fontSize: '12.5px', color: C.muted, margin: '4px 0 0' }}>Reglas de asignación agrupadas por marca · fuente de verdad: <b>AutoLead_reglas</b> · carga: <b>AutoLead_comerciales_stats</b></p>
      </div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {kpiCard(marcasVisibles.length, 'Marcas', C.brand)}
        {kpiCard(kpis.activas, 'Reglas activas', C.brand)}
        {kpiCard(kpis.inactivas, 'Inactivas', C.muted)}
        {kpiCard(kpis.crit, 'Críticos', kpis.crit ? C.rojo : C.verde)}
        {kpiCard(kpis.warn, 'Avisos', kpis.warn ? C.naranja : C.verde)}
        {kpiCard('3 / 7', 'Cred. C360 / marcas', C.brand)}
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {['all', ...MARCAS_ORDEN.filter(m => porMarca[m]), ...extras].map(m => {
          const active = filtroMarca === m;
          return (
            <div key={m} onClick={() => setFiltroMarca(m)} className="fg-nav" style={{ padding: '7px 14px', borderRadius: 10, border: 'none', background: active ? GRAD : 'transparent', color: active ? '#fff' : C.page, boxShadow: active ? '0 6px 16px rgba(0,81,135,.28)' : 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
              {m !== 'all' && <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: MARCA_COLORS[m] || C.gris }} />}
              {m === 'all' ? 'Todas' : m}
            </div>
          );
        })}
      </div>
      {marcasVisibles.map(marca => {
        const lista = (porMarca[marca] || []).slice().sort((a, b) => (a.prioridad || 99) - (b.prioridad || 99) || a.id - b.id);
        const pe = peorEstado(marca);
        const badge = pe.crit ? <Chip lvl="crit">{pe.crit} crítico{pe.crit > 1 ? 's' : ''}</Chip> : pe.warn ? <Chip lvl="warn">{pe.warn} aviso{pe.warn > 1 ? 's' : ''}</Chip> : <Chip lvl="ok">OK</Chip>;
        const colapsada = colapsadas[marca];
        const color = MARCA_COLORS[marca] || '#9aa4af';
        return (
          <div key={marca} style={{ ...tarjeta, marginBottom: '18px', overflow: 'hidden' }}>
            <div onClick={() => setColapsadas(s => ({ ...s, [marca]: !s[marca] }))} className="fg-nav" style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', cursor: 'pointer', background: '#f7f8fa', borderBottom: `1px solid ${C.line}` }}>
              <div style={{ width: '6px', height: '28px', borderRadius: 999, background: color }} />
              <h3 style={{ fontSize: '14px', fontWeight: 800, color: C.ink, margin: 0, flex: 1 }}>{marca}</h3>
              <span style={{ fontSize: '12px', color: C.muted }}>{pe.total} regla{pe.total !== 1 ? 's' : ''}</span>
              {badge}
              <span style={{ color: C.muted, fontSize: '13px', transform: colapsada ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }}>▼</span>
            </div>
            {!colapsada && (
              <div style={{ padding: '0 0 6px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                  <thead>
                    <tr>
                      <th style={celdaTitulo}>Regla</th>
                      <th style={celdaTitulo}>Tipo</th>
                      <th style={celdaTitulo}>Origen / centro</th>
                      <th style={celdaTitulo}>Criterio</th>
                      <th style={celdaTitulo}>Pool · carga activa</th>
                      <th style={celdaTitulo}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map(r => {
                      const inactiva = r.activo === false;
                      const est = inactiva ? null : estadoRegla(r);
                      const pool = poolInfo(r);
                      const crit = CRITERIOS_SELECCION.find(c => c.value === r.criterio_seleccion) || { icon: '', label: r.criterio_seleccion };
                      const tvColor = TV_COLOR[r.tipo_venta] || TV_COLOR[''];
                      return (
                        <tr key={r.id} style={{ borderTop: SEPARADOR_FILA, opacity: inactiva ? 0.5 : 1 }}>
                          <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                            <div style={{ fontWeight: 700, color: C.ink }}>{r.nombre || 'Sin nombre'}</div>
                            {r.prioridad > 1 && <div style={{ fontSize: '11px', color: C.muted }}>prioridad {r.prioridad}</div>}
                          </td>
                          <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                            <span style={pildora(tvColor)}>{r.tipo_venta || 'todos'}</span>
                          </td>
                          <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                            {r.origen ? <span style={pildora(C.muted)}>{r.origen}</span> : <span style={{ color: C.gris }}>—</span>}
                            {r.centro && <div style={{ fontSize: '11px', color: C.muted, marginTop: '3px' }}>{r.centro}</div>}
                          </td>
                          <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                            <span style={{ fontSize: '12.5px' }}>{crit.icon} {crit.label}</span>
                            {r.max_leads_activos && <div style={{ fontSize: '11px', fontWeight: 800, color: C.naranja }}>max {r.max_leads_activos}</div>}
                          </td>
                          <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                            {pool.length ? <PoolBars pool={pool} max={r.max_leads_activos} /> : <span style={{ color: C.gris }}>Sin pool</span>}
                          </td>
                          <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                            {inactiva ? <Chip lvl="neutral">inactiva</Chip> : <Chip lvl={est.lvl}>{est.txt}</Chip>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
      <h3 style={{ fontSize: '14px', fontWeight: 800, color: C.ink, margin: '28px 0 4px', letterSpacing: 0.2 }}>Mapeo marca ↔ credencial Customer360</h3>
      <p style={{ fontSize: '12.5px', color: C.muted, margin: '0 0 12px' }}>3 cuentas ADFS dan acceso a las 7 marcas Stellantis. Leapmotor no usa Customer360.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '14px' }}>
        {CREDENCIALES_C360.map((c, i) => (
          <div key={i} className="fg-card" style={{ ...tarjeta, padding: '14px 16px' }}>
            <div style={{ fontSize: '12.5px', fontWeight: 800, color: C.ink, marginBottom: '4px' }}>{c.titulo}</div>
            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '12px', color: C.brand, background: `${C.brand}0d`, border: `1px solid ${C.brand}33`, padding: '3px 8px', borderRadius: 999, display: 'inline-block', marginBottom: '8px' }}>{c.usuario}</span>
            <div style={{ fontSize: '11px', color: C.muted, marginBottom: '6px' }}>{c.tipo}</div>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {c.marcas.map(m => (
                <span key={m} style={{ fontSize: '10.5px', padding: '3px 10px', borderRadius: 999, fontWeight: 800, background: MARCA_COLORS[m] || C.gris, color: MARCA_TEXT_DARK[m] ? C.ink : '#fff' }}>{m}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: '11.5px', color: C.muted, marginTop: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <span><b style={{ color: C.page }}>Estado:</b> carga activa de cada comercial del pool vs sus compañeros.</span>
        <span><b style={{ color: C.page }}>1 comercial</b> = pool de un único comercial (intencionado).</span>
        <span>carga alta &gt; {CARGA_ALTA} · muy alta &gt; {CARGA_CRITICA}</span>
      </div>
    </div>
  );
};

// ==================== MOBILE ====================
const MobileHeader = ({ onRefresh, isLoading, onLogout }) => (<header style={{background: GRAD, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}><span style={{fontSize: '16px', fontWeight: 800, color: '#fff'}}>Auto Leads 🧠</span><div style={{display: 'flex', gap: '8px'}}><button onClick={onRefresh} style={{background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 10, padding: '8px 12px', color: '#fff', fontSize: '13px', fontWeight: 800}}>{isLoading ? '⏳' : '🔄'}</button><button onClick={onLogout} style={{background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 10, padding: '8px 12px', color: '#fff', fontSize: '13px', fontWeight: 800}}>🚪</button></div></header>);

const MobileNav = ({ activeTab, setActiveTab, inboxCount }) => (<nav style={{position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-around', padding: '8px 0 12px'}}>{[{ id: 'inbox', icon: '📥', badge: inboxCount }, { id: 'asignados', icon: '✅' }, { id: 'comerciales', icon: '👥' }, { id: 'reglas', icon: '⚙️' }].map(t => (<button key={t.id} onClick={() => setActiveTab(t.id)} className="fg-nav" style={{background: 'none', border: 'none', borderRadius: 10, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', color: activeTab === t.id ? C.brand : C.muted, position: 'relative'}}><span style={{fontSize: '20px'}}>{t.icon}</span>{t.badge > 0 && <span style={{position: 'absolute', top: '-4px', right: '-2px', ...pildora(C.naranja)}}>{t.badge}</span>}</button>))}</nav>);

// ==================== MAIN APP ====================
export default function AutoleadsApp() {
  const isMobile = useIsMobile();
  const [admin, setAdmin] = useState(null);

  // ── Sesión compartida de la suite (cookie de *.dimoauto.com) ──────────────
  // Si ya se entró en el Centro de Aplicaciones, aquí no se vuelve a pedir la
  // contraseña. Si no hay sesión compartida, o el correo no es administrador
  // activo, se enseña el login de siempre: esto solo añade un atajo.
  const [sesionProbada, setSesionProbada] = useState(false);
  useEffect(() => {
    let vivo = true;
    entrarConSesionCompartida(SUPABASE_URL, SUPABASE_ANON_KEY)
      .then((a) => { if (vivo && a) setAdmin(a); })
      .finally(() => { if (vivo) setSesionProbada(true); });
    return () => { vivo = false; };
  }, []);
  const [leads, setLeads] = useState([]);
  const [inboxLeads, setInboxLeads] = useState([]);
  const [comerciales, setComerciales] = useState([]);
  const [comercialesStats, setComercialesStats] = useState([]);
  const [reglas, setReglas] = useState([]);
  const [proximasAsignaciones, setProximasAsignaciones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(() => {
    // Recuperar estado guardado en localStorage
    const saved = localStorage.getItem('autoAssignEnabled');
    return saved === 'true';
  });
  const [autoAssignStatus, setAutoAssignStatus] = useState(null);
  const [lastAutoAssign, setLastAutoAssign] = useState(null);

  useEffect(() => { 
    (async () => { 
      setAuthLoading(true); 
      try { 
        const authUser = await supabaseAuth.getCurrentUser();
        if (authUser) {
          const adminData = await findAdminByAuthId(authUser.id);
          if (adminData) {
            setAdmin(adminData);
          } else {
            await supabaseAuth.signOut();
          }
        }
      } catch (e) { 
        console.error('Auth error:', e);
        await supabaseAuth.signOut();
      } 
      finally { setAuthLoading(false); } 
    })(); 
  }, []);

  const loadData = async () => { 
    setIsLoading(true); 
    try { 
      const [ld, ib, rg, cm, st, pa] = await Promise.all([fetchLeads(), fetchInboxLeads(), fetchReglas(), fetchComerciales(), fetchComercialesStats(), fetchProximasAsignaciones()]);
      setLeads(ld || []);
      setInboxLeads(ib || []);
      setReglas(rg || []);
      setComerciales(cm || []);
      setComercialesStats(st || []);
      setProximasAsignaciones(pa || []);
      setLastUpdate(new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })); 
    } catch (e) { 
      console.error('Error cargando datos:', e); 
    } finally { 
      setIsLoading(false); 
    } 
  };

  // ==================== AUTO-REFRESH (POLLING) ====================
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const lastInboxCountRef = useRef(0);
  const autoAssignEnabledRef = useRef(autoAssignEnabled);
  
  // Mantener ref sincronizada
  useEffect(() => {
    autoAssignEnabledRef.current = autoAssignEnabled;
  }, [autoAssignEnabled]);

  // Función para encontrar match de regla para un lead (para auto-assign)
  const findMatchForLead = useCallback((lead, reglasData, comercialesData, statsData) => {
    if (!reglasData?.length) return null;
    
    // Helper inline para parsear comerciales_pool
    const parsePoolInline = (pool) => {
      if (!pool) return [];
      if (Array.isArray(pool)) return pool.map(id => parseInt(id, 10));
      if (typeof pool === 'string') {
        try {
          const parsed = JSON.parse(pool);
          if (Array.isArray(parsed)) return parsed.map(id => parseInt(id, 10));
        } catch (e) {
          console.warn('Error parseando comerciales_pool:', pool);
        }
      }
      return [];
    };
    
    const selectBest = (pool, criterio, maxLeadsActivos = 0) => {
      const parsedPool = parsePoolInline(pool);
      if (parsedPool.length === 0) return null;

      const poolStats = parsedPool.map(id => {
        const comercial = comercialesData.find(c => c.id === id);
        const stat = statsData.find(s => s.comercial_id === id) || { leads_activos: 0, tiempo_medio_respuesta_horas: 999, tasa_conversion_30d: 0, ultimo_lead_asignado: '1970-01-01' };
        return { comercial, stat };
      }).filter(x => x.comercial && !x.comercial.agenda_bloqueada);
      if (poolStats.length === 0) return null;

      let sorted;
      switch(criterio) {
        case 'menor_carga': sorted = poolStats.sort((a, b) => a.stat.leads_activos - b.stat.leads_activos); break;
        case 'mas_rapido': sorted = poolStats.sort((a, b) => (a.stat.tiempo_medio_respuesta_horas || 999) - (b.stat.tiempo_medio_respuesta_horas || 999)); break;
        case 'mejor_conversion': sorted = poolStats.sort((a, b) => (b.stat.tasa_conversion_30d || 0) - (a.stat.tasa_conversion_30d || 0)); break;
        case 'round_robin': sorted = poolStats.sort((a, b) => new Date(a.stat.ultimo_lead_asignado || 0) - new Date(b.stat.ultimo_lead_asignado || 0)); break;
        case 'score_combinado': sorted = poolStats.map(p => ({...p, score: (100 - p.stat.leads_activos) * 0.4 + (100 - Math.min(p.stat.tiempo_medio_respuesta_horas || 100, 100)) * 0.3 + ((p.stat.tasa_conversion_30d || 0) * 100) * 0.3})).sort((a, b) => b.score - a.score); break;
        // PATCH 2026-05-11.F: respeta orden de pool y devuelve el primero con
        // leads_activos < max_leads_activos. Si todos saturados, primero del pool.
        case 'preferente_pool': {
          if (maxLeadsActivos > 0) {
            const disp = poolStats.find(p => (p.stat.leads_activos || 0) < maxLeadsActivos);
            if (disp) return disp.comercial;
          }
          return poolStats[0].comercial;
        }
        default: sorted = poolStats.sort((a, b) => a.stat.leads_activos - b.stat.leads_activos);
      }
      return sorted[0].comercial;
    };

    // PATCH 2026-05-07: mismo criterio de B2B que findMatch (UI).
    const isLeadB2B = (
      (lead.tipo_cliente && /^(empresa|b2b|flota)/i.test(String(lead.tipo_cliente)))
      || (lead.razon_social && String(lead.razon_social).trim() !== '')
    );
    const effLeadTV = isLeadB2B ? 'B2B' : (lead.tipo_venta || '');
    const normReglaTV = (rtv) => {
      if (!rtv) return '';
      const s = String(rtv).toUpperCase();
      if (s.includes('B2B') || s.includes('FLOTA')) return 'B2B';
      return s.trim();
    };
    for (const r of [...reglasData].sort((a, b) => (a.prioridad || 99) - (b.prioridad || 99))) {
      if (r.activo === false) continue;
      let ok = true;
      if (r.marca && lead.marca && r.marca !== lead.marca) ok = false;
      if (r.origen && lead.origen && r.origen !== lead.origen) ok = false;
      const reglaTV = normReglaTV(r.tipo_venta);
      if (reglaTV && effLeadTV && reglaTV !== effLeadTV) ok = false;
      if (r.centro && lead.centro && r.centro !== lead.centro) ok = false;
      if (ok) {
        const parsedPool = parsePoolInline(r.comerciales_pool);
        if (parsedPool.length > 0) {
          const comercial = selectBest(r.comerciales_pool, r.criterio_seleccion || 'score_combinado', r.max_leads_activos || 0);
          if (comercial) return { comercial, regla: r };
        } else if (r.comercial_id) {
          const c = comercialesData.find(x => x.id === r.comercial_id);
          if (c) return { comercial: c, regla: r };
        }
      }
    }
    return null;
  }, []);

  // Función de auto-asignación extraída para reutilizar
  const runAutoAssign = useCallback(async (leadsToProcess, reglasData, comercialesData, statsData) => {
    if (!autoAssignEnabledRef.current) return 0;
    
    const pendingLeads = leadsToProcess.filter(l => !l.comercial_asignado && l.estado !== 'asignado' && l.estado !== 'rechazado');
    if (pendingLeads.length === 0) return 0;

    let assigned = 0;
    for (const lead of pendingLeads) {
      const match = findMatchForLead(lead, reglasData, comercialesData, statsData);
      if (match) {
        // Verificar que el lead aún no esté asignado (doble verificación)
        const freshLead = await fetchInboxLeadById(lead.id);
        
        if (!freshLead || freshLead.comercial_asignado || freshLead.estado === 'asignado') {
          continue;
        }
        
        // Verificar que no exista ya un lead de este inbox_id
        // Filtramos en servidor con ilike() para evitar el límite 1000 de
        // PostgREST: solo nos interesan los lead_ids que contengan
        // 'inbox-{lead.id}'.
        let exists = false;
        try {
          const checkResp = await fetch(
            `${SUPABASE_URL}/rest/v1/leads?select=lead_id&lead_id=ilike.*inbox-${encodeURIComponent(lead.id)}*`,
            { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' } }
          );
          if (checkResp.ok) {
            const checkData = await checkResp.json();
            exists = Array.isArray(checkData) && checkData.length > 0;
          }
        } catch (_) { exists = false; }

        // PATCH 2026-05-05: dedup adicional por (email, telefono, marca).
        // Antes este loop solo verificaba por lead_id=inbox-X. Si varios
        // polls concurrentes (5s) pasaban el check y el unique index no
        // bloqueaba el INSERT (telefono=NULL es distinto de NULL en uniques),
        // se creaban N copias del mismo lead. Incidente Julinho Mendes
        // 2026-05-05: 53 copias en 27 segundos.
        if (!exists && lead.email) {
          try {
            const baseHdr = { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' };
            const telPart = lead.telefono
              ? `telefono=eq.${encodeURIComponent(lead.telefono)}`
              : `telefono=is.null`;
            const marcaPart = lead.marca
              ? `&marca=eq.${encodeURIComponent(lead.marca)}`
              : '';
            const dupResp = await fetch(
              `${SUPABASE_URL}/rest/v1/leads?select=id&email=eq.${encodeURIComponent(lead.email)}&${telPart}${marcaPart}`,
              { headers: baseHdr }
            );
            if (dupResp.ok) {
              const dupData = await dupResp.json();
              if (Array.isArray(dupData) && dupData.length > 0) exists = true;
            }
          } catch (_) { /* silent */ }
        }

        if (exists) {
          await updateInboxLead(lead.id, { 
            estado: 'asignado', 
            comercial_asignado: match.comercial.nombre, 
            fecha_asignacion: new Date().toISOString() 
          });
          continue;
        }
        
        const newLead = {
          lead_id: await getNextLeadId(),
          origen: lead.origen,
          estado_cita: 'Pendiente', 
          usuario_alta: 'AutoAssign', 
          fecha_creacion: new Date().toISOString(), 
          vendedor_nombre: match.comercial.nombre, 
          centro: match.comercial.centro, 
          nombre: lead.nombre, 
          apellidos: lead.apellidos, 
          telefono: lead.telefono, 
          movil: lead.movil, 
          email: lead.email, 
          marca: lead.marca, 
          modelo: lead.modelo, 
          tipo_venta: (
            // Si el lead es de empresa, lo persistimos como B2B aunque venga como VN
            (lead.tipo_cliente && /^(empresa|b2b|flota)/i.test(String(lead.tipo_cliente)))
            || (lead.razon_social && String(lead.razon_social).trim() !== '')
          ) ? 'B2B' : (lead.tipo_venta || 'VN'),
          motor: lead.motor,
          matricula: lead.matricula,
          kilometros: lead.kilometros
        };
        
        const { error } = await insertLead(newLead);
        if (!error) {
          await updateInboxLead(lead.id, { 
            estado: 'asignado', 
            comercial_asignado: match.comercial.nombre, 
            fecha_asignacion: new Date().toISOString() 
          });
          
          if (match.comercial.email) {
            await sendLeadEmail(lead, match.comercial);
          }
          assigned++;
        }
      }
    }
    return assigned;
  }, [findMatchForLead]);
  
  useEffect(() => {
    if (!admin) return;
    
    setRealtimeConnected(true);
    
    // Polling cada 5 segundos para detectar nuevos leads
    const pollInterval = setInterval(async () => {
      try {
        const [newInbox, newReglas, newComerciales, newStats, newLeads] = await Promise.all([
          fetchInboxLeads(),
          fetchReglas(),
          fetchComerciales(),
          fetchComercialesStats(),
          fetchLeads()
        ]);

        // PATCH 2026-04-30: refrescar `leads` en cada poll. Sin esto, el match
        // de AssignedLeadsView (inbox <-> leads) usa un `leads` obsoleto y
        // marca como "Duplicado" cualquier lead recién insertado hasta el F5.
        if (newLeads) setLeads(newLeads);

        if (newInbox) {
          const pendingNew = newInbox.filter(l => l.estado !== 'asignado' && l.estado !== 'rechazado').length;
          
          // Si hay más leads pendientes que antes, actualizar
          if (pendingNew > lastInboxCountRef.current) {
            setInboxLeads(newInbox);
            setLastUpdate('🔴 ' + new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }));
            
            // Ejecutar auto-assign inmediatamente si está habilitado
            if (autoAssignEnabledRef.current && newReglas && newComerciales) {
              const assigned = await runAutoAssign(newInbox, newReglas, newComerciales, newStats || []);
              if (assigned > 0) {
                setLastAutoAssign({ count: assigned, time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) });
                // Recargar para reflejar cambios
                const updatedInbox = await fetchInboxLeads();
                if (updatedInbox) setInboxLeads(updatedInbox);
              }
            }
          } else if (newInbox.length !== inboxLeads.length) {
            // Si cambió el total, actualizar silenciosamente
            setInboxLeads(newInbox);
          }
          lastInboxCountRef.current = pendingNew;
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 5000); // Cada 5 segundos
    
    return () => clearInterval(pollInterval);
  }, [admin]);

  // Guardar estado de auto-asignación en localStorage
  useEffect(() => {
    localStorage.setItem('autoAssignEnabled', autoAssignEnabled.toString());
  }, [autoAssignEnabled]);

  // Auto-asignación automática cuando hay leads pendientes y está activado
  useEffect(() => {
    if (!autoAssignEnabled || !admin || isLoading) return;
    if (!inboxLeads.length || !reglas.length || !comerciales.length) return;
    
    // Filtrar leads sin comercial asignado
    const pendingLeads = inboxLeads.filter(l => !l.comercial_asignado && l.estado !== 'asignado' && l.estado !== 'rechazado');
    if (pendingLeads.length === 0) return;

    const executeAutoAssign = async () => {
      const assigned = await runAutoAssign(inboxLeads, reglas, comerciales, comercialesStats);
      if (assigned > 0) {
        setLastAutoAssign({ count: assigned, time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) });
        await loadData();
      }
    };

    // Pequeño delay para evitar múltiples ejecuciones
    const timer = setTimeout(executeAutoAssign, 300);
    return () => clearTimeout(timer);
  }, [autoAssignEnabled, inboxLeads, reglas, comerciales, comercialesStats, admin, isLoading, runAutoAssign]);

  useEffect(() => { if (admin) loadData(); }, [admin]);

  const handleAssign = async (inboxLead, comercial, sendEmail = true) => {
    // ============= VERIFICACIÓN 1: Estado local =============
    if (inboxLead.comercial_asignado) {
      alert(`Este lead ya fue asignado a ${inboxLead.comercial_asignado}`);
      return;
    }
    
    // ============= VERIFICACIÓN 2: Estado FRESH desde BD =============
    // Recargar el lead desde la base de datos para verificar su estado actual
    try {
      const freshLead = await fetchInboxLeadById(inboxLead.id);
      
      if (!freshLead) {
        alert('El lead ya no existe en el sistema');
        await loadData();
        return;
      }
      
      if (freshLead.comercial_asignado) {
        alert(`Este lead ya fue asignado a ${freshLead.comercial_asignado}`);
        await loadData();
        return;
      }
      
      if (freshLead.estado === 'asignado' || freshLead.estado === 'rechazado') {
        alert(`Este lead ya tiene estado: ${freshLead.estado}`);
        await loadData();
        return;
      }
    } catch (error) {
      console.error('Error verificando estado del lead:', error);
      alert('Error verificando el estado del lead. Por favor intenta de nuevo.');
      return;
    }
    
    // ============= VERIFICACIÓN 3: Duplicado por datos del cliente =============
    // PATCH 2026-04-30: alineado con idx_unique_lead_identity
    // (nombre,email,telefono,marca). Mismo cliente con marca distinta NO es
    // duplicado y debe poder asignarse. Además ignoramos `nombre` porque el
    // inbox a veces llega con 'Sin nombre' antes de que n8n extraiga el JSON.
    try {
      // Filtramos en servidor por los criterios concretos en vez de traer
      // toda la tabla y filtrar en cliente: así evitamos el límite 1000 de
      // PostgREST y la query es mucho más eficiente (<10 resultados).
      const existingLeads = [];
      const baseHeaders = { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' };
      const cols = 'foreign_id,email,telefono,marca';
      if (inboxLead.foreign_id) {
        try {
          const resp = await fetch(`${SUPABASE_URL}/rest/v1/leads?select=${cols}&foreign_id=eq.${encodeURIComponent(inboxLead.foreign_id)}`, { headers: baseHeaders });
          if (resp.ok) {
            const d = await resp.json();
            if (Array.isArray(d)) existingLeads.push(...d);
          }
        } catch (_) {}
      }
      // PATCH 2026-05-05: si inbox.telefono es NULL la query previa se
      //   saltaba entera y el dedup no detectaba duplicados → 53 copias
      //   del lead Julinho Mendes. Ahora buscamos con telefono=is.null
      //   cuando no hay teléfono.
      if (inboxLead.email) {
        try {
          const telPart = inboxLead.telefono
            ? `telefono=eq.${encodeURIComponent(inboxLead.telefono)}`
            : `telefono=is.null`;
          const resp = await fetch(`${SUPABASE_URL}/rest/v1/leads?select=${cols}&email=eq.${encodeURIComponent(inboxLead.email)}&${telPart}`, { headers: baseHeaders });
          if (resp.ok) {
            const d = await resp.json();
            if (Array.isArray(d)) existingLeads.push(...d);
          }
        } catch (_) {}
      }

      const sameMarca = (a, b) => {
        if (!a || !b) return true;
        return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
      };

      // Buscar por (email + telefono + marca) y opcionalmente foreign_id
      const isDuplicateByData = existingLeads.some(l =>
        (inboxLead.foreign_id && l.foreign_id &&
         String(l.foreign_id) === String(inboxLead.foreign_id) &&
         sameMarca(l.marca, inboxLead.marca)) ||
        (l.email && inboxLead.email && l.email === inboxLead.email &&
         l.telefono === inboxLead.telefono &&
         sameMarca(l.marca, inboxLead.marca))
      );
      
      if (isDuplicateByData) {
        // Lead duplicado, solo actualizar inbox para que desaparezca de pendientes
        await updateInboxLead(inboxLead.id, { 
          estado: 'asignado', 
          comercial_asignado: comercial.nombre, 
          fecha_asignacion: new Date().toISOString() 
        });
        await loadData();
        alert('Este lead ya existe en el sistema. Se ha actualizado su estado.');
        return;
      }
      
      // ============= VERIFICACIÓN 4: Duplicado por lead_id único =============
      // Generar lead_id único basado en inbox_id para garantizar unicidad
      const uniqueLeadId = `lead-inbox-${inboxLead.id}-${Date.now()}`;
      
      const isDuplicateByLeadId = existingLeads.some(l => l.lead_id && l.lead_id.includes(`inbox-${inboxLead.id}`));
      
      if (isDuplicateByLeadId) {
        // Ya se creó un lead desde este inbox_lead
        await updateInboxLead(inboxLead.id, { 
          estado: 'asignado', 
          comercial_asignado: comercial.nombre, 
          fecha_asignacion: new Date().toISOString() 
        });
        await loadData();
        alert('Este lead ya fue procesado anteriormente.');
        return;
      }
    } catch (error) {
      console.error('Error verificando duplicados:', error);
    }
    
    // ============= PASO 5: Crear el nuevo lead =============
    const newLead = {
      lead_id: await getNextLeadId(), // ID secuencial: max existente + 1
      origen: inboxLead.origen, 
      estado_cita: 'Pendiente', 
      usuario_alta: admin?.nombre || 'Auto', 
      fecha_creacion: new Date().toISOString(), 
      vendedor_nombre: comercial.nombre, 
      centro: comercial.centro, 
      nombre: inboxLead.nombre, 
      apellidos: inboxLead.apellidos, 
      telefono: inboxLead.telefono, 
      movil: inboxLead.movil, 
      email: inboxLead.email, 
      marca: inboxLead.marca, 
      modelo: inboxLead.modelo, 
      tipo_venta: (
        // Persistir como B2B cuando el lead es de empresa
        (inboxLead.tipo_cliente && /^(empresa|b2b|flota)/i.test(String(inboxLead.tipo_cliente)))
        || (inboxLead.razon_social && String(inboxLead.razon_social).trim() !== '')
      ) ? 'B2B' : (inboxLead.tipo_venta || 'VN'), 
      motor: inboxLead.motor, 
      matricula: inboxLead.matricula, 
      kilometros: inboxLead.kilometros 
    };
    
    const { error: insertError } = await insertLead(newLead);
    
    if (insertError) {
      console.error('Error insertando lead:', insertError);
      alert('Error al crear el lead. Por favor intenta de nuevo.');
      return;
    }
    
    // ============= PASO 6: Actualizar AutoLead_inbox =============
    const updateData = { 
      estado: 'asignado', 
      comercial_asignado: comercial.nombre, 
      fecha_asignacion: new Date().toISOString() 
    };
    
    const { error: updateError } = await updateInboxLead(inboxLead.id, updateData);
    
    if (updateError) {
      console.error('Error actualizando inbox:', updateError);
      // El lead ya se creó, pero no pudimos actualizar el inbox
      // Intentar revertir o al menos notificar
      alert('Lead creado pero hubo un problema actualizando el estado. Revisa con un administrador.');
      await loadData();
      return;
    }
    
    // ============= PASO 7: Enviar email =============
    if (sendEmail && comercial.email) {
      await sendLeadEmail(inboxLead, comercial);
    }
    
    // ============= PASO 8: Recargar datos =============
    await loadData();
    setSelectedLead(null);
  };

  const handleReject = async (lead) => { await updateInboxLead(lead.id, { estado: 'rechazado' }); await loadData(); setSelectedLead(null); };
  const handleDeleteInboxLead = async (lead) => {
    const nombre = `${lead.nombre || ''} ${lead.apellidos || ''}`.trim() || lead.email || lead.telefono || lead.movil || `lead #${lead.id}`;
    if (!window.confirm(`¿Eliminar definitivamente "${nombre}" del inbox?\n\nEsta acción no se puede deshacer.`)) return;
    const { error } = await deleteInboxLead(lead.id);
    if (error) { alert('Error eliminando el lead: ' + (error.message || error)); return; }
    await loadData();
    setSelectedLead(null);
  };
  const handleAddRegla = async (r) => { await insertRegla(r); await loadData(); };
  const handleUpdateRegla = async (id, r) => { await updateRegla(id, r); await loadData(); };
  const handleDeleteRegla = async (id) => { await deleteRegla(id); await loadData(); };
  const handleLogout = async () => { borrarSesion(); await supabaseAuth.signOut(); setAdmin(null); };

  const inboxCount = useMemo(() => inboxLeads.filter(isPendingInboxLead).length, [inboxLeads]);
  const assignedCount = useMemo(() => inboxLeads.filter(l => l.comercial_asignado).length, [inboxLeads]);

  if (!admin && !sesionProbada) return <Entrando />;
  if (authLoading) return <LoginScreen onLogin={setAdmin} isLoading={true} error={null} />;
  if (!admin) return <LoginScreen onLogin={setAdmin} isLoading={false} error={authError} />;

  if (isMobile) {
    const pendientes = inboxLeads.filter(isPendingInboxLead);
    const ultimosAsignados = leads.slice(0, 30);
    const fmtFecha = (s) => { if (!s) return '-'; try { return new Date(s).toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return '-'; } };
    return (
      <div style={{minHeight: '100vh', background: C.bg, paddingBottom: '78px'}}>
        <MobileHeader onRefresh={loadData} isLoading={isLoading} onLogout={handleLogout} />
        <div style={{padding: '12px 14px 4px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px'}}>
          {/* Tarjetas de cifra con la forma de StatCard (etiqueta arriba, cifra abajo) */}
          <div style={{...tarjeta, padding: '9px 10px', textAlign: 'center'}}>
            <div style={etiquetaCifra}>Pendientes</div>
            <div style={{fontSize: '20px', fontWeight: 800, color: C.naranja, lineHeight: 1.05, marginTop: '4px'}}>{inboxCount}</div>
          </div>
          <div style={{...tarjeta, padding: '9px 10px', textAlign: 'center'}}>
            <div style={etiquetaCifra}>Asignados</div>
            <div style={{fontSize: '20px', fontWeight: 800, color: C.verde, lineHeight: 1.05, marginTop: '4px'}}>{assignedCount}</div>
          </div>
          <div style={{...tarjeta, padding: '9px 10px', textAlign: 'center'}}>
            <div style={etiquetaCifra}>Comerciales</div>
            <div style={{fontSize: '20px', fontWeight: 800, color: C.brand, lineHeight: 1.05, marginTop: '4px'}}>{comerciales.length}</div>
          </div>
        </div>
        <div style={{padding: '10px 14px'}}>
          {activeTab === 'inbox' && (pendientes.length === 0
            ? <EmptyState icon="📭" title="Sin leads pendientes de asignar" />
            : pendientes.map(lead => (
              <div key={lead.id} className="fg-card" style={{...tarjeta, padding: '14px', marginBottom: '10px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px'}}>
                  <div style={{fontWeight: 800, fontSize: '13.5px', color: C.ink}}>{lead.nombre || 'Sin nombre'}</div>
                  <span style={pildora(getOrigenColor(lead.origen))}>{lead.origen || 'manual'}</span>
                </div>
                <div style={{fontSize: '12px', color: C.muted, marginBottom: '4px'}}>{lead.marca || '-'} · {lead.tipo_venta || '-'}{lead.modelo ? ' · ' + lead.modelo : ''}</div>
                <div style={{fontSize: '11px', color: C.faint, marginBottom: '10px'}}>{lead.telefono || lead.email || '-'} · {fmtFecha(lead.created_at || lead.fecha_recepcion)}</div>
                <select defaultValue="" onChange={(e) => { const c = comerciales.find(x => x.id === parseInt(e.target.value)); if (c) handleAssign(lead, c); }} style={{...campo, width: '100%', padding: '10px 12px', background: C.bg}}>
                  <option value="">Asignar a comercial...</option>
                  {comerciales.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.centro})</option>)}
                </select>
              </div>
            ))
          )}
          {activeTab === 'asignados' && (ultimosAsignados.length === 0
            ? <EmptyState icon="✅" title="Aún no hay leads asignados" />
            : ultimosAsignados.map(l => (
              <div key={l.id} className="fg-card" style={{...tarjeta, padding: '12px 14px', marginBottom: '8px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                  <div>
                    <div style={{fontWeight: 800, fontSize: '12.5px', color: C.ink}}>{l.nombre || 'Sin nombre'}</div>
                    <div style={{fontSize: '11px', color: C.muted, marginTop: '2px'}}>{l.marca || '-'} · {l.modelo || ''}</div>
                  </div>
                  <div style={{textAlign: 'right'}}>
                    <div style={{fontSize: '11.5px', color: C.brand, fontWeight: 800}}>{l.vendedor_nombre || '-'}</div>
                    <div style={{fontSize: '10.5px', color: C.faint, marginTop: '2px'}}>{fmtFecha(l.fecha_asignacion || l.created_at)}</div>
                  </div>
                </div>
              </div>
            ))
          )}
          {activeTab === 'comerciales' && (comerciales.length === 0
            ? <EmptyState icon="👥" title="No hay comerciales configurados" />
            : comerciales.map(c => {
                const stats = comercialesStats?.[c.id] || {};
                return (
                  <div key={c.id} className="fg-card" style={{...tarjeta, padding: '12px 14px', marginBottom: '8px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <div style={{fontWeight: 800, fontSize: '12.5px', color: C.ink}}>{c.nombre}</div>
                        <div style={{fontSize: '11px', color: C.muted, marginTop: '2px'}}>{c.centro || '-'} · {c.marca || 'todas'}</div>
                      </div>
                      <div style={{textAlign: 'right'}}>
                        <div style={{fontSize: '20px', fontWeight: 800, color: C.brand, lineHeight: 1.05}}>{stats.total ?? '-'}</div>
                        <div style={etiquetaCifra}>leads</div>
                      </div>
                    </div>
                  </div>
                );
              })
          )}
          {activeTab === 'reglas' && (reglas.length === 0
            ? <EmptyState icon="⚙️" title="No hay reglas configuradas" />
            : reglas.map(r => (
              <div key={r.id} className="fg-card" style={{...tarjeta, padding: '12px 14px', marginBottom: '8px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                  <div style={{flex: 1}}>
                    <div style={{fontWeight: 800, fontSize: '12.5px', color: C.ink}}>{r.nombre || 'Sin nombre'}</div>
                    <div style={{fontSize: '11px', color: C.muted, marginTop: '2px'}}>
                      {[r.marca, r.tipo_venta, r.origen, r.centro].filter(Boolean).join(' · ') || 'Sin filtros'}
                    </div>
                  </div>
                  <span style={pildora(C.brand)}>P{r.prioridad || '-'}</span>
                </div>
              </div>
            ))
          )}
          {!['inbox', 'asignados', 'comerciales', 'reglas'].includes(activeTab) && (
            <EmptyState icon="📱" title={'Sección "' + activeTab + '" solo disponible en escritorio'} />
          )}
        </div>
        <MobileNav activeTab={activeTab} setActiveTab={setActiveTab} inboxCount={inboxCount} />
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', background: C.bg}}>
      <Header
        onRefresh={loadData} 
        isLoading={isLoading} 
        lastUpdate={lastUpdate} 
        admin={admin} 
        onLogout={handleLogout} 
        onOpenChangePassword={() => setShowChangePassword(true)} 
        autoAssignEnabled={autoAssignEnabled}
        onToggleAutoAssign={() => setAutoAssignEnabled(!autoAssignEnabled)}
        lastAutoAssign={lastAutoAssign}
        realtimeConnected={realtimeConnected}
      />
      <Tabs activeTab={activeTab} setActiveTab={setActiveTab} inboxCount={inboxCount} assignedCount={assignedCount} />
      <div style={{display: 'flex', flex: 1, minHeight: 0}}>
        <main className="fg-fade" style={{flex: 1, minWidth: 0, overflowY: 'auto'}}>
          {activeTab === 'inbox' && <InboxView inboxLeads={inboxLeads} comerciales={comerciales} reglas={reglas} comercialesStats={comercialesStats} onAssign={handleAssign} onReject={handleReject} onDelete={handleDeleteInboxLead} onRefresh={loadData} selectedLead={selectedLead} setSelectedLead={setSelectedLead} />}
          {activeTab === 'asignados' && <AssignedLeadsView leads={leads} inboxLeads={inboxLeads} onDelete={handleDeleteInboxLead} />}
          {activeTab === 'comerciales' && <ComercialesView leads={leads} comerciales={comerciales} comercialesStats={comercialesStats} />}
          {activeTab === 'reglas' && <ReglasView reglas={reglas} comerciales={comerciales} comercialesStats={comercialesStats} proximasAsignaciones={proximasAsignaciones} leads={leads} onAddRegla={handleAddRegla} onDeleteRegla={handleDeleteRegla} onUpdateRegla={handleUpdateRegla} />}
          {activeTab === 'vista-marca' && <VistaPorMarcaView reglas={reglas} comerciales={comerciales} comercialesStats={comercialesStats} />}
          {activeTab === 'origenes' && <OrigenesView leads={leads} inboxLeads={inboxLeads} />}
        </main>
      </div>
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} admin={admin} />}
    </div>
  );
}
